import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import StructureGridCard, { AddStructureCard } from '../components/proposals/StructureGridCard';
import StructureDetailModal from '../components/proposals/StructureDetailModal';
import KitProposalWizard from '../components/proposals/KitProposalWizard';
import {
  calculateTotals,
  applyProfitMargin,
  getEffectiveProjectOverheadPercent,
  countGlobalCostShareEligible,
  syncNodeSetsForProjectOverhead,
  getProposalDiscountAmount,
  usesGlobalStructureCosting,
  createDefaultGlobalCostsForNewCustomProposal,
  designIncludedInKitForNewProposalWrite,
  resolveDesignIncludedInKit,
  zeroNodeFreightAndOverheadForGlobalCosting,
} from '../utils/proposalCalculations';
import { computeExclusiveStaffEconomics, nodeSetsForExclusiveSelection } from '../utils/proposalExclusiveEconomics';
import { computeBondingAmount, effectiveBondingPercent, formatBondingPercent } from '../utils/proposalBonding';
import { applySpeciesTimberKitBillableDelta } from '../utils/proposalSpeciesProduction';
import {
  estimatingRaw,
  lockDesignEstimating,
  stampDesignEstimatingTotal,
} from '../utils/proposalDesignBreakdown';
import {
  getSignedContractFooterTotals,
  getSignedSpeciesDisplayLabel,
  parseSpeciesSelectionsForPublicTotals,
  getSignedCommercialListTotal,
} from '../utils/proposalPublicGrandTotals';
import { X, ExternalLink, FileText, Upload, AlertCircle, Link2, ChevronLeft, Download, ChevronDown, Briefcase, BadgePercent, ClipboardList, TreePine, History, Box, ShieldCheck, Send, Loader2, Bot, Bug, Wrench, LayoutList, Columns2 } from 'lucide-react';
import OpsReviewCommentsPanel from '../components/proposals/OpsReviewCommentsPanel';
import {
  subscribeOpsReviewMeta,
  subscribeOpsReviewComments,
  addOpsReviewComment,
  canCommentOnOpsReview,
  hasOpsReviewActivity,
  revokeOpsReviewApproval,
} from '../utils/proposalOpsReview';
import {
  requestProposalReview,
  subscribeProposalReviewRequest,
  type ProposalReviewRequestSnapshot,
} from '../utils/biddingAgentService';
import type { ProposalOpsReviewComment, ProposalOpsReviewMeta, ProposalOpsReviewNodeType } from '../types';
import '../components/proposals/ProposalOpsReview.css';
import GlobalCostsModal from '../components/proposals/GlobalCostsModal';
import ProposalDiscountModal from '../components/proposals/ProposalDiscountModal';
import TimberSupplierSummaryModal from '../components/proposals/TimberSupplierSummaryModal';
import ProposalReviewModal from '../components/proposals/ProposalReviewModal';
import ProposalVersionHistoryModal from '../components/proposals/ProposalVersionHistoryModal';
import AppHeader from '../components/ui/AppHeader';
import ProposalSummary from '../components/proposals/ProposalSummary';
import { useUndoRedo } from '../components/proposals/useProposalUndo';
import { useCRM } from '../utils/CRMContext';
import { useAuth } from '../utils/AuthContext';
import {
  projectHubViewPath,
  useProposalBuilderProjectHubHotkey,
} from '../hooks/useProjectHubViewModeHotkeys';
import { usePairedScrollSync } from '../hooks/usePairedScrollSync';
import { useCompareSyncHotkey } from '../hooks/useCompareSyncHotkey';
import { parseLegacyTitle } from '../utils/projectName';
import { subscribeChangeOrdersForDeal } from '../utils/changeOrders';
import { sumApprovedChangeOrders } from '../utils/contractValue';
import { useLeadTimeContext } from '../context/LeadTimeContext';
import { getProposalRates, getSetting, ProposalRateSettings } from '../utils/appSettings';
import {
  TimberKit,
  ProposalBuilderData,
  ProposalBuilderVersionDoc,
  ProposalNodeSet,
  ProposalChecklist,
  ProposalPhase,
  ProposalPhaseStatus,
  DEFAULT_PROPOSAL_CHECKLIST,
  createDefaultNodeSet,
  GlobalCosts,
  ProposalDiscount,
  ProposalCostingModel,
  DesignEstimatingData,
  ChangeOrder,
} from '../types';
import { buildPaymentScheduleBreakdown } from '../utils/paymentSchedule';
import PaymentScheduleBreakdown from '../components/proposals/PaymentScheduleBreakdown';
import ProposalPhasePanel from '../components/proposals/ProposalPhasePanel';
import { isPhasedProjectsFeatureEnabled } from '../utils/phasedProjectsFeature';
import {
  buildDefaultPhases,
  createProposalPhase,
  getNodeSetPhaseId,
  getOrderedPhases,
  getProposalPhaseById,
} from '../utils/proposalPhases';
import { db } from '../utils/firebase';
import { doc, setDoc, onSnapshot, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { parseProposalCSV, groupItemsByGroup, convertToTimberListItems, CSVParseResult } from '../utils/proposalCSVParser';
import {
  applyHardwareItemsToNodeSets,
  parseHardwareCsv,
  type HardwareCsvParseResult,
} from '../utils/proposalHardwareCsvParser';
import { subscribeToHardwareItems, subscribeToTimberItems, type HardwareItem, type TimberItem } from '../utils/materialsDatabaseService';
import { syncProposalNodeSetsCatalogPrices } from '../utils/proposalCatalogPriceSync';
import HardwareListApplyModal from '../components/proposals/HardwareListApplyModal';
import { parseTimberMaterialColumn } from '../utils/timberMaterialParser';
import { subscribeProjectFiles, filterProjectTimberCsvs } from '../utils/projectFileService';
import { portalHostedAssetHref } from '../utils/portalAssetUrl';
import type { ProjectFile } from '../types';
import {
  getStructureNamesWithTimberListIssues,
  getStructureNamesWithTimberValidationIssues,
  structureHasShortOrderLengthTimber,
  structureHasTimberListIssues,
} from '../utils/timberListValidation';
import {
  applyClientExclusiveToNodeSet,
  applyClientOptionalToNodeSet,
  canMarkClientOptional,
  structureIsExclusive,
} from '../utils/proposalExclusiveGroups';
import { diffProposalTimber, type ProposalTimberDiff } from '../utils/proposalTimberDiff';
import { applyProposalTimberDiff, type TimberDiffSelections } from '../utils/proposalTimberApply';
import { resyncDbPricesForStructures } from '../utils/proposalTimberPricing';
import type { ProposalTimberDefaults } from '../utils/proposalTimberDefaults';
import { enabledProposalStructures } from '../utils/proposalStructureRefs';
import { buildStructureMentionProjectOptions } from '../utils/structureMentionProjectOptions';
import TimberListUpdateModal from '../components/proposals/TimberListUpdateModal';
import TimberComparePickModal, { type TimberComparePickPayload } from '../components/proposals/TimberComparePickModal';
import StructureComparePicker from '../components/proposals/StructureComparePicker';
import type { StructureCompareSession } from '../utils/structureLineCompare';
import { buildStructureFromComparePick } from '../utils/proposalTimberComparePick';
import InternalReviewPdfDocument, { InternalReviewData } from '../components/proposals/InternalReviewPdfDocument';
import type { ReviewJoineryPerson } from '../utils/internalReviewPdfLines';
import PricingDebugPdfDocument from '../components/proposals/PricingDebugPdfDocument';
import { generatePricingDebugData } from '../utils/pricingDebugUtils';
import { pdf } from '@react-pdf/renderer';
import {
  downloadTimberSupplierCsv,
  openTimberPrintWindow,
  printTimberSupplierToWindow,
  TIMBER_SUPPLIER_POPUP_BLOCKED_MSG,
  timberSupplierExportHasContent,
} from '../utils/supplierTimberExport';
import {
  downloadDeckingSupplierCsv,
  printDeckingSupplierToWindow,
  deckingExportHasContent,
} from '../utils/supplierDeckingExport';
import { pushProposalTimberToRfqStaging } from '../utils/rfqHubFileReleaseService';
import { createProposalVersion, formatProposalVersionTimestamp, formatProposalVersionSource, getProposalVersion } from '../utils/proposalVersioning';
import './ProposalBuilderPage.css';
import { removeUndefinedDeep } from '../utils/removeUndefinedDeep';

// Track which deals have been initialized (persists across remounts)
// const initializedDeals = new Set<string>();

interface ProposalState {
  nodeSets: ProposalNodeSet[];
  profitMargin: number;
  checklist: ProposalChecklist;
  installOptional?: boolean;
  designIncludedInKit?: boolean;
  isRomPricing?: boolean;
  projectSquareFootage?: number;
  // Phased Project mode (beta)
  phasingEnabled?: boolean;
  phases?: ProposalPhase[];
  // Kit proposal tracking
  isKitProposal?: boolean;
  sourceKitId?: string;
  sourceKitName?: string;
  isCustomized?: boolean;
  kitAvailableSpecies?: string[];  // All species available in the kit
  offeredSpecies?: string[];  // Species options offered to client (subset of kitAvailableSpecies)
  activeSpecies?: string;  // Currently selected species for pricing preview
  kitPricing?: Array<{ speciesName: string; totalCost: number }>;  // Species pricing options from kit
  // Global project costs (optional, for large/complex projects)
  globalCosts?: GlobalCosts;
  // Promotional discount (flat dollars off the client total)
  discount?: ProposalDiscount;
  /** Missing / legacy = per-structure freight + overhead. `global` = new custom proposals. */
  costingModel?: ProposalCostingModel;
  designEstimating?: DesignEstimatingData;
}

function appendNodeSetsFromCsvResult(state: ProposalState, csvParseResult: CSVParseResult): ProposalState {
  const groupedItems = groupItemsByGroup(csvParseResult.items);
  const newNodeSets: ProposalNodeSet[] = [];
  Object.entries(groupedItems).forEach(([groupName, items], index) => {
    const timberListItems = convertToTimberListItems(items);
    const totalBoardFeet = timberListItems.reduce((sum, item) => sum + item.boardFeet, 0);
    const nodeSet = createDefaultNodeSet(groupName);
    nodeSet.id = `nodeset-${Date.now()}-${index}`;
    nodeSet.materials = {
      ...nodeSet.materials,
      timberList: timberListItems,
      totalBoardFeet: Math.round(totalBoardFeet * 100) / 100,
    };
    nodeSet.production = {
      ...nodeSet.production,
      boardFeet: Math.round(totalBoardFeet * 100) / 100,
    };
    newNodeSets.push(nodeSet);
  });
  return {
    ...state,
    nodeSets: [...state.nodeSets, ...newNodeSets],
  };
}

const ProposalBuilderInner: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { enhancedDeals, updateEnhancedDeal, pipelines, timberProjects, leads, crmUsers } = useCRM();
  const { user } = useAuth();
  /** Internal proposal tools (Internal Review, debug) — admins use the builder too but were not included before. */
  const isStaffBuilderUser = !!user && (user.role === 'employee' || user.role === 'admin');
  const isAdminBuilderUser = user?.role === 'admin';
  const { setActiveLead } = useLeadTimeContext();

  // Refs for handlers to avoid stale closures
  const handleRenameRef = useRef<(nodeSetId: string, newName: string) => void>(() => { });
  const handleDeleteRef = useRef<(nodeSetId: string) => void>(() => { });

  // Find the deal
  const deal = enhancedDeals.find(d => d.id === dealId);

  // Resolve the TimberProject for this deal — projectFiles are keyed by
  // TimberProject.id, NOT dealId, so we need this id to query Hub files.
  // Returns null when the deal hasn't been promoted to a project yet
  // (rare for proposals, but possible for very early-stage deals).
  const projectIdForFiles = React.useMemo(
    () => (dealId ? timberProjects.find(p => p.dealId === dealId)?.id || null : null),
    [dealId, timberProjects],
  );

  const openProjectHubTable = useCallback(() => {
    if (!projectIdForFiles) return;
    navigate(projectHubViewPath(projectIdForFiles, 'table'));
  }, [projectIdForFiles, navigate]);

  useProposalBuilderProjectHubHotkey({
    enabled: !!projectIdForFiles,
    projectId: projectIdForFiles,
    onOpenHub: openProjectHubTable,
  });

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proposalData, setProposalData] = useState<ProposalBuilderData | null>(null);
  const [rates, setRates] = useState<ProposalRateSettings | null>(null);
  const [approvedChangeOrderTotal, setApprovedChangeOrderTotal] = useState<number>(0);
  const [dealChangeOrders, setDealChangeOrders] = useState<ChangeOrder[]>([]);
  const [showContractSchedule, setShowContractSchedule] = useState(false);
  const [checklistCompleted, setChecklistCompleted] = useState(false);
  const [selectedNode, setSelectedNode] = useState<{ nodeSetId: string; nodeType: string } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

  // Kit Proposal Wizard state
  const [showKitWizard, setShowKitWizard] = useState(false);
  const [showGlobalCosts, setShowGlobalCosts] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showComparePicker, setShowComparePicker] = useState(false);
  const [compareSession, setCompareSession] = useState<StructureCompareSession | null>(null);
  const compareBtnRef = useRef<HTMLButtonElement>(null);
  const comparePanesRef = useRef<HTMLDivElement>(null);
  const [compareScrollSync, setCompareScrollSync] = useState(true);
  usePairedScrollSync(comparePanesRef, '.detail-modal-body', !!compareSession && compareScrollSync);
  useCompareSyncHotkey(!!compareSession, () => setCompareScrollSync(on => !on));

  // CSV Import state
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [csvParseResult, setCsvParseResult] = useState<CSVParseResult | null>(null);
  const [hardwareParseResult, setHardwareParseResult] = useState<HardwareCsvParseResult | null>(null);
  const [importing, setImporting] = useState(false);
  /** When user imports from the project timber list URL, we dismiss the banner after a successful import. */
  const csvImportSourceRef = useRef<'project' | 'local' | null>(null);
  /**
   * Project-linked timber CSVs surfaced in the picker view of the import
   * modal. Subscribed only while the modal is open to avoid an idle
   * Firestore listener for every proposal page load.
   */
  const [projectTimberCsvs, setProjectTimberCsvs] = useState<ProjectFile[]>([]);
  const [projectTimberCsvsLoading, setProjectTimberCsvsLoading] = useState(false);
  /** Row-level loading indicator while fetching/parsing the picked CSV. */
  const [pickingProjectCsvId, setPickingProjectCsvId] = useState<string | null>(null);
  /**
   * Latest 3D HTML model from the project's version-controlled files.
   * Powers the small preview button in the header. Subscribed once while
   * the page is mounted (small overhead, single listener).
   */
  const [latestHtmlModel, setLatestHtmlModel] = useState<ProjectFile | null>(null);
  const [showHtmlModelPopup, setShowHtmlModelPopup] = useState(false);
  /**
   * Internal-review PDF preview state. Holds an object-URL for an in-memory
   * blob so the user can review (and *optionally* download) the report
   * instead of being force-downloaded on every click. The URL is revoked when
   * the popup closes or the page unmounts to avoid leaking blob memory.
   */
  const [internalReviewPdf, setInternalReviewPdf] = useState<{
    url: string;
    filename: string;
    generating?: boolean;
  } | null>(null);
  const [internalReviewLoading, setInternalReviewLoading] = useState(false);
  const [debugPricingLoading, setDebugPricingLoading] = useState(false);
  /**
   * Diff modal state. Driven explicitly by either the banner button or the
   * Tools menu — there is no longer an auto-trigger that opens this on its own.
   * (The previous auto-import effect was removed; all timber CSV intake is now explicit.)
   */
  const [updateModal, setUpdateModal] = useState<null | {
    diff: ProposalTimberDiff;
    parsed: CSVParseResult;
    source: 'project' | 'local';
    fileMeta?: { fileId?: string; fileName?: string; uploadedAt?: Date | null; uploadedByName?: string };
  }>(null);
  /** Side-by-side compare & pick modal — cherry-pick lines into a new structure. */
  const [comparePickModal, setComparePickModal] = useState<null | {
    parsed: CSVParseResult;
    source: 'project' | 'local';
    fileMeta?: { fileId?: string; fileName?: string; uploadedAt?: Date | null; uploadedByName?: string };
    /** When set (Add Timber on a structure), append only to that node set. */
    lockToNodeSetId?: string;
  }>(null);
  /** Tools → Timber Input is unlocked; structure Add Timber locks to this id. */
  const [timberInputLockToNodeSetId, setTimberInputLockToNodeSetId] = useState<string | null>(null);
  /** Lightweight transient toast for post-apply / up-to-date confirmations. */
  const [flashMsg, setFlashMsg] = useState<string | null>(null);
  const flashMsgTimerRef = useRef<number | null>(null);
  const showFlash = useCallback((msg: string) => {
    setFlashMsg(msg);
    if (flashMsgTimerRef.current) window.clearTimeout(flashMsgTimerRef.current);
    flashMsgTimerRef.current = window.setTimeout(() => setFlashMsg(null), 3500);
  }, []);
  const [timberImportIssueModal, setTimberImportIssueModal] = useState<null | {
    csvErrors: string[];
    structureNamesWithIssues: string[];
    /** Auto-import: supplier RFQ was not auto-opened only because of order length &lt; 8'. */
    blockedAutoRfqOnlyForOrderLength?: boolean;
  }>(null);
  const [timberExportWarningModal, setTimberExportWarningModal] = useState<null | { structureNames: string[] }>(null);

  // Tools dropdown state
  const [showToolsMenu, setShowToolsMenu] = useState(false);

  // OPS review workflow
  const [opsReviewMeta, setOpsReviewMeta] = useState<ProposalOpsReviewMeta | null>(null);
  const [opsReviewComments, setOpsReviewComments] = useState<ProposalOpsReviewComment[]>([]);
  const [showOpsCommentsPanel, setShowOpsCommentsPanel] = useState(false);
  // Pre-send bidding-agent proposal review (Mode 2) — background queue.
  const [proposalReviewReq, setProposalReviewReq] = useState<ProposalReviewRequestSnapshot | null>(null);
  const [proposalReviewBusy, setProposalReviewBusy] = useState(false);
  const opsReviewMetaRef = useRef<ProposalOpsReviewMeta | null>(null);
  const [showTimberSupplierSummary, setShowTimberSupplierSummary] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [stagingRfqBusy, setStagingRfqBusy] = useState(false);
  const [snapshotPreview, setSnapshotPreview] = useState<ProposalBuilderVersionDoc | null>(null);
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);
  const [lastTimberDefaults, setLastTimberDefaults] = useState<ProposalTimberDefaults | null>(null);
  const autoAppliedTimberDefaultsRef = useRef<Set<string>>(new Set());

  // Time tracking: set active lead when building proposal for a deal
  useEffect(() => {
    const leadId = deal?.leadId;
    if (leadId && dealId) {
      setActiveLead(leadId, dealId, proposalData?.id);
    } else {
      setActiveLead(null);
    }
    return () => setActiveLead(null);
  }, [deal?.leadId, dealId, proposalData?.id, setActiveLead]);

  // Flag to prevent onSnapshot from overwriting local edits during active editing
  const isLocalEditRef = useRef(false);
  // Track the last saved timestamp to ignore our own writes
  const lastSaveTimeRef = useRef<number>(0);
  // Track if save is in progress to prevent concurrent saves
  const saveInProgressRef = useRef(false);
  // Track if there are pending changes that need saving after current save completes
  const pendingSaveRef = useRef(false);
  // One-shot guard so the auto-create-on-empty branch only fires once per mount,
  // even if the Firestore subscription emits multiple times before the initial
  // proposal write lands.
  const initialCreateRef = useRef(false);




  // Memoize initial state to prevent infinite loop
  const initialProposalState = React.useMemo(() => ({
    nodeSets: [],
    profitMargin: 40,
    checklist: DEFAULT_PROPOSAL_CHECKLIST,
    installOptional: false,
    // Undo/redo seed only — existing docs load via resolveDesignIncludedInKit;
    // blank/kit creates set designIncludedInKitForNewProposalWrite() explicitly.
    designIncludedInKit: false,
    isRomPricing: false,
    projectSquareFootage: undefined,
  }), []);

  // Undo/redo for proposal state
  const {
    state: proposalState,
    setState: setProposalState,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useUndoRedo<ProposalState>(initialProposalState);

  const [hasChanges, setHasChanges] = useState(false);
  const hasChangesRef = useRef(false);
  // showUnsendPopup removed — send/unsend is now in ClientPortalManagement

  // Computed: is proposal sent/locked for editing?
  const isSent = proposalData?.status === 'sent' || proposalData?.status === 'opened' || proposalData?.status === 'approved';
  const isApproved = proposalData?.status === 'approved';

  useEffect(() => {
    if (!compareSession || !proposalState) return;
    const leftOk = proposalState.nodeSets.some(ns => ns.id === compareSession.leftNodeSetId);
    const right = compareSession.right;
    const rightOk = right.source === 'local'
      ? proposalState.nodeSets.some(ns => ns.id === right.nodeSetId)
      : true;
    if (!leftOk || !rightOk) {
      setCompareSession(null);
      setShowComparePicker(false);
    }
  }, [compareSession, proposalState]);

  const canRequestOpsReview =
    isStaffBuilderUser &&
    !isSent &&
    opsReviewMeta?.status !== 'pending';

  const opsReviewCommentCount = opsReviewComments.length;
  const canOpsReviewComment = canCommentOnOpsReview(opsReviewMeta, user?.id, user?.role);

  const opsStructureMentionProjectOptions = useMemo(
    () =>
      buildStructureMentionProjectOptions(
        timberProjects,
        new Map(enhancedDeals.filter((d) => d.id).map((d) => [d.id!, d] as const)),
      ),
    [timberProjects, enhancedDeals],
  );

  const opsDefaultStructures = useMemo(
    () => enabledProposalStructures(proposalState?.nodeSets),
    [proposalState?.nodeSets],
  );

  const showOpsReviewBtn =
    isStaffBuilderUser &&
    (!isSent || hasOpsReviewActivity(opsReviewMeta, opsReviewCommentCount));

  const opsReviewSummaryLabel =
    opsReviewMeta?.status === 'pending'
      ? 'Pending OPS Review'
      : opsReviewMeta?.status === 'approved'
        ? 'Review complete'
        : 'OPS Review';

  const handleAddOpsReviewComment = useCallback(
    async (
      nodeSetId: string,
      nodeSetName: string,
      message: string,
      nodeType: ProposalOpsReviewNodeType = 'structure',
      nodeLabel = 'General'
    ) => {
      if (!dealId || !user || !message.trim()) return;
      await addOpsReviewComment({
        dealId,
        nodeSetId,
        nodeSetName,
        nodeType,
        nodeLabel,
        message: message.trim(),
        authorId: user.id,
        authorName: user.name || user.email || 'Staff',
      });
    },
    [dealId, user]
  );

  const structureSharePercents = React.useMemo(() => {
    if (!proposalState?.nodeSets?.length) return {} as Record<string, number>;
    const enabled = proposalState.nodeSets.filter(ns => ns.enabled !== false);
    const rawFor = (ns: ProposalNodeSet) => {
      const qty = ns.quantity || 1;
      const unit =
        ns.design.total +
        ns.production.total +
        ns.materials.total +
        ns.freight.total +
        ns.overhead.total +
        ns.install.total +
        ns.travel.total;
      return unit * qty;
    };
    const totals = enabled.map(rawFor);
    const projectTotal = totals.reduce((sum, t) => sum + t, 0);
    const map: Record<string, number> = {};
    enabled.forEach((ns, i) => {
      map[ns.id] =
        projectTotal > 0 ? (totals[i] / projectTotal < 0.01 && totals[i] > 0 ? 1 : Math.round((totals[i] / projectTotal) * 100)) : 0;
    });
    return map;
  }, [proposalState?.nodeSets]);

  /** Footer + public math: use sign-time snapshot — never recompute from live nodeSets / CRM rates. */
  const isSignedCommercial =
    proposalData?.status === 'approved' && !!proposalData?.signature;

  const signedContractFooterTotals = React.useMemo(
    () => (proposalData ? getSignedContractFooterTotals(proposalData) : null),
    [proposalData],
  );

  const signedSpeciesDisplayLabel = React.useMemo(
    () => (proposalData ? getSignedSpeciesDisplayLabel(proposalData) : null),
    [proposalData],
  );

  const signedSpeciesSelections = React.useMemo(() => {
    if (!proposalData || !isSignedCommercial) return null;
    return parseSpeciesSelectionsForPublicTotals(proposalData);
  }, [proposalData, isSignedCommercial]);

  // Keep hasChangesRef in sync with hasChanges state
  useEffect(() => {
    hasChangesRef.current = hasChanges;
  }, [hasChanges]);

  // Refs to store data for building flow


  // Load rates
  useEffect(() => {
    const loadRates = async () => {
      const loadedRates = await getProposalRates();
      setRates(loadedRates);
    };
    loadRates();
  }, [dealId]);

  // Phased projects beta flag (global default from settings)
  const [phasedProjectsBetaDefault, setPhasedProjectsBetaDefault] = useState(false);
  useEffect(() => {
    getSetting<boolean>('phasedProjectsDefaultEnabled', false)
      .then(v => setPhasedProjectsBetaDefault(!!v))
      .catch(() => setPhasedProjectsBetaDefault(false));
  }, []);

  // Approved change orders for this deal (contract banner + payment schedule on signed proposals).
  useEffect(() => {
    if (!dealId) return;
    const unsub = subscribeChangeOrdersForDeal(dealId, orders => {
      setDealChangeOrders(orders);
      setApprovedChangeOrderTotal(sumApprovedChangeOrders(orders));
    });
    return unsub;
  }, [dealId]);

  // Payment schedule from the signed base — same util as public page & portal.
  const contractScheduleBreakdown = React.useMemo(
    () =>
      signedContractFooterTotals
        ? buildPaymentScheduleBreakdown(signedContractFooterTotals.total, dealChangeOrders)
        : null,
    [signedContractFooterTotals, dealChangeOrders],
  );

  // OPS review meta + comments
  useEffect(() => {
    if (!dealId || !isStaffBuilderUser) return;
    const unsubMeta = subscribeOpsReviewMeta(dealId, setOpsReviewMeta);
    const unsubComments = subscribeOpsReviewComments(dealId, setOpsReviewComments);
    return () => {
      unsubMeta();
      unsubComments();
    };
  }, [dealId, isStaffBuilderUser]);

  useEffect(() => {
    opsReviewMetaRef.current = opsReviewMeta;
  }, [opsReviewMeta]);

  // Live status of the background bidding-agent proposal review.
  useEffect(() => {
    if (!dealId || !isAdminBuilderUser) return;
    return subscribeProposalReviewRequest(dealId, setProposalReviewReq);
  }, [dealId, isAdminBuilderUser]);

  const reviewStatus = proposalReviewReq?.status;
  const isReviewRunning = reviewStatus === 'pending' || reviewStatus === 'processing';
  const hasReviewResult = !!deal?.proposalReview;

  const handleAgentReview = useCallback(async () => {
    if (!dealId || !isAdminBuilderUser || proposalReviewBusy || isReviewRunning) return;
    setProposalReviewBusy(true);
    try {
      await requestProposalReview(dealId, { leadId: deal?.leadId });
      showFlash('Proposal sent to the bidding agent for review.');
    } catch (err) {
      showFlash(err instanceof Error ? err.message : 'Could not queue proposal review.');
    } finally {
      setProposalReviewBusy(false);
    }
  }, [dealId, deal?.leadId, isAdminBuilderUser, proposalReviewBusy, isReviewRunning, showFlash]);

  // When project overhead % is on, ensure per-structure overhead days are zero (draft proposals only).
  useEffect(() => {
    if (!rates || !proposalState || !proposalData) return;
    // Never auto-recalculate sent/signed proposals on open — preserves commercial terms.
    if (isSent) return;
    if (getEffectiveProjectOverheadPercent(proposalState.globalCosts) <= 0) return;

    const synced = syncNodeSetsForProjectOverhead(
      proposalState.nodeSets,
      proposalState.globalCosts,
      rates
    );
    if (synced === proposalState.nodeSets) return;

    isLocalEditRef.current = true;
    setProposalState(prev => (prev ? { ...prev, nodeSets: synced } : prev));
    setHasChanges(true);
  }, [
    rates,
    proposalState,
    proposalData,
    isSent,
    proposalState?.globalCosts?.projectOverheadPercentEnabled,
    proposalState?.globalCosts?.projectOverheadPercent,
    setProposalState,
  ]);

  // Load or create proposal data
  useEffect(() => {
    if (!dealId) return;

    // Reset edit flag on mount
    isLocalEditRef.current = false;

    // Clear initialization for this deal when component mounts fresh
    // (only if navigating to a different deal)

    const proposalRef = doc(db, 'proposalBuilders', dealId);

    const unsubscribe = onSnapshot(proposalRef, (snapshot) => {
      const now = Date.now();

      // Skip updates that come right after our own save (within 5 seconds)
      // Prevents Firestore echo from overwriting local state on slow connections
      if (now - lastSaveTimeRef.current < 5000) {
        console.log(`%c[onSnapshot] SKIPPED — within 5s of save (${now - lastSaveTimeRef.current}ms ago)`, 'color: gray');
        setLoading(false);
        return;
      }

      // Skip if actively editing (user is typing/dragging)
      if (isLocalEditRef.current) {
        console.log(`%c[onSnapshot] SKIPPED — isLocalEditRef=true`, 'color: gray');
        setLoading(false);
        return;
      }

      // Skip if user has pending unsaved changes (prevents overwriting edits during debounce period)
      if (hasChangesRef.current) {
        console.log(`%c[onSnapshot] SKIPPED — hasChangesRef=true`, 'color: gray');
        setLoading(false);
        return;
      }

      console.log(`%c[onSnapshot] APPLYING Firestore data to state`, 'color: red; font-weight: bold');

      if (snapshot.exists()) {
        const data = snapshot.data() as ProposalBuilderData;
        const nodeSets = data.nodeSets || [];

        setProposalData(data);
        setProposalState({
          nodeSets,
          profitMargin: data.profitMargin || 40,
          checklist: data.checklist || DEFAULT_PROPOSAL_CHECKLIST,
          // Kit tracking fields
          isKitProposal: data.isKitProposal,
          sourceKitId: data.sourceKitId,
          sourceKitName: data.sourceKitName,
          isCustomized: data.isCustomized,
          // Use kitAvailableSpecies if saved, otherwise fall back to offeredSpecies
          kitAvailableSpecies: data.kitAvailableSpecies || data.offeredSpecies,
          offeredSpecies: data.offeredSpecies,
          // Default activeSpecies to first species if not set (signed proposals use sign-time snapshot)
          activeSpecies: (() => {
            const signed = data.status === 'approved' || !!(data as { signature?: unknown }).signature;
            if (signed) {
              const label = getSignedSpeciesDisplayLabel(data);
              if (label) return label;
              const { kitSelectedSpecies } = parseSpeciesSelectionsForPublicTotals(data);
              if (kitSelectedSpecies) return kitSelectedSpecies;
            }
            return data.activeSpecies || (data.offeredSpecies?.[0]) || (data.kitAvailableSpecies?.[0]);
          })(),
          kitPricing: data.kitPricing,
          globalCosts: data.globalCosts,
          costingModel: data.costingModel,
          discount: data.discount,
          installOptional: data.installOptional,
          // Missing/undefined on legacy docs must stay false (not the new-create default).
          designIncludedInKit: resolveDesignIncludedInKit(data.designIncludedInKit),
          isRomPricing: data.isRomPricing,
          projectSquareFootage: data.projectSquareFootage && data.projectSquareFootage > 0
            ? data.projectSquareFootage
            : undefined,
          phasingEnabled: data.phasingEnabled,
          phases: data.phases,
          designEstimating: data.designEstimating,
        });
        setChecklistCompleted(true);

        setChecklistCompleted(true);
      } else {
        // New proposal — kit pipelines still funnel through the kit wizard
        // (it picks the source kit + species). For custom proposals we now
        // skip the data-gathering checklist entirely and drop straight into
        // the builder with one default node set; PMs can fill in scope as
        // they go instead of front-loading a long form.
        const dealPipeline = pipelines.find(p => p.id === deal?.pipelineId);
        const isKitPipeline = dealPipeline?.name === 'Timber Frame Kits';

        if (isKitPipeline) {
          setShowKitWizard(true);
        } else if (!initialCreateRef.current) {
          initialCreateRef.current = true;
          const initialNodeSet = createDefaultNodeSet(deal?.title || 'Main Structure');
          const newProposalState: ProposalState = {
            nodeSets: [initialNodeSet],
            profitMargin: rates?.defaultProfitMargin || 40,
            checklist: DEFAULT_PROPOSAL_CHECKLIST,
            costingModel: 'global',
            globalCosts: createDefaultGlobalCostsForNewCustomProposal(),
            designIncludedInKit: designIncludedInKitForNewProposalWrite(),
          };
          setProposalState(newProposalState);
          setChecklistCompleted(true);
          setHasChanges(true);
          // Fire-and-forget; the snapshot's 5s self-write guard suppresses
          // the echo from this initial save.
          saveProposal(newProposalState).catch(err =>
            console.error('[ProposalBuilder] Failed to seed initial proposal:', err),
          );
        }
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [dealId, setProposalState, pipelines, deal?.pipelineId]);



  // Build React Flow nodes/edges from node sets (callback version for effects)


  // Rebuild flow only for local edits (not remote changes from onSnapshot)


  // Handle kit wizard completion
  const handleKitWizardComplete = async (nodeSets: ProposalNodeSet[], selectedSpecies: string[], selectedKit: TimberKit) => {
    setShowKitWizard(false);
    setChecklistCompleted(true);

    // Get all species from the kit's speciesPricing
    const allKitSpecies = selectedKit.speciesPricing?.map(sp => sp.speciesName) || selectedSpecies;

    // Create proposal state from kit with kit tracking
    const newProposalState: ProposalState = {
      nodeSets,
      profitMargin: selectedKit.profitMargin || rates?.defaultProfitMargin || 40,
      checklist: {
        ...DEFAULT_PROPOSAL_CHECKLIST,
        // Mark kit-related items as complete
        projectTypeConfirmed: true,
        timberSpeciesSelected: true,
        timberListAvailable: true,
      },
      // Kit tracking - locks editing to Install & Travel only
      isKitProposal: true,
      sourceKitId: selectedKit.id,
      sourceKitName: selectedKit.name,
      isCustomized: false,
      kitAvailableSpecies: allKitSpecies,  // All species from the kit
      offeredSpecies: selectedSpecies,  // Initially selected species to offer
      activeSpecies: selectedSpecies[0] || allKitSpecies[0],  // Default to first species for pricing
      kitPricing: selectedKit.speciesPricing?.map(sp => ({
        speciesName: sp.speciesName,
        totalCost: sp.billableTotal,
        structurePricing: (sp as any).structurePricing  // Include per-structure pricing
      })),
      designIncludedInKit: designIncludedInKitForNewProposalWrite(),
    };

    setProposalState(newProposalState);
    setHasChanges(true);

    // Save to Firestore
    await saveProposal(newProposalState);
  };

  // Add new node set
  const handleAddNodeSet = useCallback(() => {
    if (!proposalState) return;

    // For kit proposals, show coming soon popup instead of adding a structure
    if (proposalState.isKitProposal) {
      setShowComingSoonPopup(true);
      return;
    }

    isLocalEditRef.current = true;

    // Calculate position for new node set (below the last one)
    // Even though we aren't using the canvas, we can keep the data consistent just in case
    let newY = 0;
    const lastNodeSet = proposalState.nodeSets[proposalState.nodeSets.length - 1];
    if (lastNodeSet) {
      const lastHeight = lastNodeSet.dimensions?.height || 240;
      newY = lastNodeSet.position.y + lastHeight + 50; // 50px gap
    }

    const newNodeSet = createDefaultNodeSet(`Structure ${proposalState.nodeSets.length + 1}`);
    newNodeSet.position = { x: 50, y: newY };

    // Ensure unique ID just in case
    newNodeSet.id = `nodeset-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    const newState = {
      ...proposalState,
      nodeSets: [...proposalState.nodeSets, newNodeSet],
    };
    setProposalState(newState);
    setHasChanges(true);

  }, [proposalState, setProposalState]);

  // Toggle structure enabled/disabled (exclude from proposal without deleting)
  const handleToggleEnabled = useCallback((nodeSetId: string) => {
    if (!proposalState) return;
    isLocalEditRef.current = true;
    const newState = {
      ...proposalState,
      nodeSets: proposalState.nodeSets.map(ns =>
        ns.id === nodeSetId ? { ...ns, enabled: ns.enabled === false ? true : false } : ns
      ),
    };
    setProposalState(newState);
    setHasChanges(true);
  }, [proposalState, setProposalState]);

  const handleToggleClientOptional = useCallback((nodeSetId: string) => {
    if (!proposalState) return;
    const target = proposalState.nodeSets.find(ns => ns.id === nodeSetId);
    if (!target || target.enabled === false) return;

    const turningOn = target.clientOptional !== true;
    if (turningOn && !canMarkClientOptional(proposalState.nodeSets, nodeSetId)) {
      alert('At least one structure must remain required. Mark another structure as non-optional first.');
      return;
    }

    isLocalEditRef.current = true;
    const newState = {
      ...proposalState,
      nodeSets: proposalState.nodeSets.map(ns =>
        ns.id === nodeSetId ? applyClientOptionalToNodeSet(ns, turningOn) : ns
      ),
    };
    setProposalState(newState);
    setHasChanges(true);
  }, [proposalState, setProposalState]);

  const handleToggleClientExclusive = useCallback((nodeSetId: string) => {
    if (!proposalState) return;
    const target = proposalState.nodeSets.find(ns => ns.id === nodeSetId);
    if (!target || target.enabled === false) return;

    isLocalEditRef.current = true;
    const newState = {
      ...proposalState,
      nodeSets: proposalState.nodeSets.map(ns =>
        ns.id === nodeSetId
          ? applyClientExclusiveToNodeSet(ns, !structureIsExclusive(ns))
          : ns
      ),
    };
    setProposalState(newState);
    setHasChanges(true);
  }, [proposalState, setProposalState]);

  // ── Phased Project mode handlers ──
  const applyPhaseState = useCallback((updater: (prev: ProposalState) => ProposalState) => {
    if (!proposalState) return;
    isLocalEditRef.current = true;
    setProposalState(updater(proposalState));
    setHasChanges(true);
  }, [proposalState, setProposalState]);

  const handleTogglePhasing = useCallback((enabled: boolean) => {
    if (!proposalState) return;
    if (isSignedCommercial) return;
    if (!enabled && (proposalState.phases || []).some(p => p.status === 'committed')) {
      alert('Phasing cannot be turned off — a phase has already been committed.');
      return;
    }
    applyPhaseState(prev => ({
      ...prev,
      phasingEnabled: enabled,
      phases: enabled && (!prev.phases || prev.phases.length === 0) ? buildDefaultPhases() : prev.phases,
    }));
  }, [proposalState, applyPhaseState, isSignedCommercial]);

  const handleAddPhase = useCallback(() => {
    applyPhaseState(prev => {
      const phases = prev.phases || [];
      const maxOrder = phases.reduce((m, p) => Math.max(m, p.order ?? 0), -1);
      return { ...prev, phases: [...phases, createProposalPhase(maxOrder + 1)] };
    });
  }, [applyPhaseState]);

  const handleRenamePhase = useCallback((phaseId: string, name: string) => {
    applyPhaseState(prev => ({
      ...prev,
      phases: (prev.phases || []).map(p => (p.id === phaseId ? { ...p, name } : p)),
    }));
  }, [applyPhaseState]);

  const handleMovePhase = useCallback((phaseId: string, direction: -1 | 1) => {
    applyPhaseState(prev => {
      const ordered = getOrderedPhases({ phases: prev.phases || [] });
      const idx = ordered.findIndex(p => p.id === phaseId);
      const swapWith = idx + direction;
      if (idx < 0 || swapWith < 0 || swapWith >= ordered.length) return prev;
      if (ordered[swapWith].status === 'committed') return prev;
      const next = [...ordered];
      [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
      return { ...prev, phases: next.map((p, i) => ({ ...p, order: i })) };
    });
  }, [applyPhaseState]);

  const handleDeletePhase = useCallback((phaseId: string) => {
    applyPhaseState(prev => {
      const phases = prev.phases || [];
      const target = phases.find(p => p.id === phaseId);
      if (!target || target.status === 'committed' || phases.length <= 1) return prev;
      const remaining = getOrderedPhases({ phases: phases.filter(p => p.id !== phaseId) })
        .map((p, i) => ({ ...p, order: i }));
      const basePhaseId = remaining[0]?.id;
      return {
        ...prev,
        phases: remaining,
        // Orphaned structures fall back to the base phase explicitly.
        nodeSets: prev.nodeSets.map(ns =>
          ns.phaseId === phaseId ? { ...ns, phaseId: basePhaseId } : ns,
        ),
      };
    });
  }, [applyPhaseState]);

  const handleSetPhaseStatus = useCallback((phaseId: string, status: ProposalPhaseStatus) => {
    applyPhaseState(prev => ({
      ...prev,
      phases: (prev.phases || []).map(p =>
        p.id === phaseId && p.status !== 'committed'
          ? { ...p, status, ...(status === 'offered' ? { offeredAt: new Date() } : {}) }
          : p,
      ),
    }));
  }, [applyPhaseState]);

  const handleAssignStructurePhase = useCallback((nodeSetId: string, phaseId: string) => {
    if (!proposalState) return;
    const target = proposalState.nodeSets.find(ns => ns.id === nodeSetId);
    if (!target) return;
    const currentPhase = getProposalPhaseById(
      { phases: proposalState.phases || [] },
      getNodeSetPhaseId({ phases: proposalState.phases || [] }, target),
    );
    const nextPhase = getProposalPhaseById({ phases: proposalState.phases || [] }, phaseId);
    if (currentPhase?.status === 'committed' || nextPhase?.status === 'committed') {
      alert('Structures cannot move into or out of a committed phase.');
      return;
    }
    applyPhaseState(prev => ({
      ...prev,
      nodeSets: prev.nodeSets.map(ns => (ns.id === nodeSetId ? { ...ns, phaseId } : ns)),
    }));
  }, [proposalState, applyPhaseState]);

  const phasedFeatureVisible = isPhasedProjectsFeatureEnabled({
    globalDefault: phasedProjectsBetaDefault,
    proposalPhasingEnabled: proposalState?.phasingEnabled,
  });

  const openComparePickModal = useCallback((
    parsed: CSVParseResult,
    source: 'project' | 'local',
    fileMeta?: { fileId?: string; fileName?: string; uploadedAt?: Date | null; uploadedByName?: string },
  ) => {
    csvImportSourceRef.current = source;
    setShowCSVImport(false);
    setCsvParseResult(null);
    setComparePickModal({
      parsed,
      source,
      fileMeta,
      ...(timberInputLockToNodeSetId ? { lockToNodeSetId: timberInputLockToNodeSetId } : {}),
    });
  }, [timberInputLockToNodeSetId]);

  const openTimberInput = useCallback((lockToNodeSetId?: string) => {
    setTimberInputLockToNodeSetId(lockToNodeSetId ?? null);
    setShowCSVImport(true);
  }, []);

  const closeTimberInputPicker = useCallback(() => {
    setShowCSVImport(false);
    setCsvParseResult(null);
    csvImportSourceRef.current = null;
    setTimberInputLockToNodeSetId(null);
  }, []);

  // CSV Import + update modal: subscribe to project timber CSVs while either
  // the import picker or the diff modal needs the version list.
  // NOTE: projectFiles are keyed by TimberProject.id, not dealId — we use
  // `projectIdForFiles` resolved above (null if the deal has no project yet).
  useEffect(() => {
    const needsTimberCsvs = (showCSVImport && !csvParseResult) || !!updateModal || !!comparePickModal;
    if (!needsTimberCsvs || !projectIdForFiles) {
      if (!needsTimberCsvs) {
        setProjectTimberCsvs([]);
        setProjectTimberCsvsLoading(false);
      }
      return;
    }
    setProjectTimberCsvsLoading(true);
    const unsub = subscribeProjectFiles(projectIdForFiles, (files) => {
      setProjectTimberCsvs(filterProjectTimberCsvs(files));
      setProjectTimberCsvsLoading(false);
    });
    return () => unsub();
  }, [showCSVImport, csvParseResult, updateModal, comparePickModal, projectIdForFiles]);

  // Latest HTML 3D model subscription. Single page-mounted listener so the
  // header button can light up without any extra clicks. Picks the most
  // recently uploaded HTML head version across all version streams.
  useEffect(() => {
    if (!projectIdForFiles) {
      setLatestHtmlModel(null);
      return;
    }
    const unsub = subscribeProjectFiles(projectIdForFiles, (files) => {
      const htmlHeads = files
        .filter(f => f.kindTag === 'html' && f.url)
        // Prefer head versions when the upload is part of a stream; legacy
        // flat uploads (no streamId) also qualify because every one of them
        // is effectively a head of its own implicit history.
        .filter(f => !f.streamId || f.isHeadVersion === true)
        .sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
      setLatestHtmlModel(htmlHeads[0] || null);
    });
    return () => unsub();
  }, [projectIdForFiles]);

  // Timber Input: open the picked project CSV in Compare & pick.
  const handlePickProjectCsv = useCallback(async (file: ProjectFile) => {
    if (!file.url || pickingProjectCsvId) return;
    setPickingProjectCsvId(file.id || null);
    try {
      const res = await fetch(file.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const result = parseProposalCSV(text, { trueDimensions: file.trueDimensions === true });
      openComparePickModal(result, 'project', {
        fileId: file.id,
        fileName: file.name,
        uploadedAt: file.uploadedAt,
        uploadedByName: file.uploadedByName,
      });
    } catch (e) {
      console.error('[CSV Import] pick project file failed', e);
      alert('Could not load that CSV. Try again from Project Files.');
    } finally {
      setPickingProjectCsvId(null);
    }
  }, [openComparePickModal, pickingProjectCsvId]);

  /** Switch the active CSV inside the timber update modal without closing it. */
  const handleSwitchTimberUpdateSource = useCallback(async (file: ProjectFile) => {
    if (!file.url || !proposalState || pickingProjectCsvId) return;
    if (file.id === updateModal?.fileMeta?.fileId) return;
    setPickingProjectCsvId(file.id || null);
    try {
      const res = await fetch(file.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const parsed = parseProposalCSV(text, { trueDimensions: file.trueDimensions === true });
      const diff = diffProposalTimber(proposalState, parsed);
      setUpdateModal({
        diff,
        parsed,
        source: 'project',
        fileMeta: {
          fileId: file.id,
          fileName: file.name,
          uploadedAt: file.uploadedAt,
          uploadedByName: file.uploadedByName,
        },
      });
    } catch (e) {
      console.error('[Timber update] switch source failed', e);
      alert('Could not load that CSV. Try again, or download it from Project Files and upload manually.');
    } finally {
      setPickingProjectCsvId(null);
    }
  }, [proposalState, pickingProjectCsvId, updateModal?.fileMeta?.fileId]);

  /** Switch CSV source inside the compare & pick modal. */
  const handleSwitchComparePickSource = useCallback(async (file: ProjectFile) => {
    if (!file.url || pickingProjectCsvId) return;
    if (file.id === comparePickModal?.fileMeta?.fileId) return;
    setPickingProjectCsvId(file.id || null);
    try {
      const res = await fetch(file.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const parsed = parseProposalCSV(text, { trueDimensions: file.trueDimensions === true });
      setComparePickModal({
        parsed,
        source: 'project',
        fileMeta: {
          fileId: file.id,
          fileName: file.name,
          uploadedAt: file.uploadedAt,
          uploadedByName: file.uploadedByName,
        },
        ...(comparePickModal?.lockToNodeSetId
          ? { lockToNodeSetId: comparePickModal.lockToNodeSetId }
          : {}),
      });
    } catch (e) {
      console.error('[Compare pick] switch source failed', e);
      alert('Could not load that CSV. Try again, or download it from Project Files and upload manually.');
    } finally {
      setPickingProjectCsvId(null);
    }
  }, [pickingProjectCsvId, comparePickModal?.fileMeta?.fileId, comparePickModal?.lockToNodeSetId]);

  const handleCreateStructureFromComparePick = useCallback(async (payload: TimberComparePickPayload) => {
    if (!proposalState || !comparePickModal) return;
    try {
      const { state: newState, newNodeSetId, lineCount } = buildStructureFromComparePick({
        structureName: payload.structureName,
        targetNodeSetId: payload.targetNodeSetId,
        proposalLineIds: payload.proposalLineIds,
        csvLineKeys: payload.csvLineKeys,
        state: proposalState,
        parsed: comparePickModal.parsed,
      });
      isLocalEditRef.current = true;
      setProposalState(newState as ProposalState);
      setHasChanges(true);
      setComparePickModal(null);
      setTimberInputLockToNodeSetId(null);

      try {
        const repriced = await resyncDbPricesForStructures(newState, [newNodeSetId]);
        if (repriced !== newState) {
          isLocalEditRef.current = true;
          setProposalState(repriced as ProposalState);
          setHasChanges(true);
        }
      } catch (e) {
        console.error('[Compare pick] DB resync failed', e);
      }

      showFlash(
        payload.targetNodeSetId
          ? `Added ${lineCount} line${lineCount === 1 ? '' : 's'} to "${payload.structureName}"`
          : `Created structure "${payload.structureName}" with ${lineCount} line${lineCount === 1 ? '' : 's'}`,
      );
    } catch (e) {
      console.error('[Compare pick] create structure failed', e);
      alert(e instanceof Error ? e.message : 'Could not add timber lines');
    }
  }, [proposalState, comparePickModal, setProposalState, showFlash]);

  // CSV Import: Confirm and create structures
  const handleCSVImportConfirm = useCallback(async () => {
    if (!proposalState || !csvParseResult) return;

    setImporting(true);
    isLocalEditRef.current = true;

    try {
      const newState = appendNodeSetsFromCsvResult(proposalState, csvParseResult);
      const added = newState.nodeSets.length - proposalState.nodeSets.length;

      setProposalState(newState);
      setHasChanges(true);
      setShowCSVImport(false);
      setCsvParseResult(null);

      const source = csvImportSourceRef.current;
      csvImportSourceRef.current = null;
      if (source === 'project' && dealId && proposalData?.latestTimberListPrompt?.fileId) {
        const fid = proposalData.latestTimberListPrompt.fileId;
        await updateDoc(doc(db, 'proposalBuilders', dealId), {
          timberListAppliedToProposalFileId: fid,
        });
        setProposalData(prev => prev ? {
          ...prev,
          timberListAppliedToProposalFileId: fid,
        } : null);
      }

      const csvErrors = csvParseResult.errors;
      const structureNames = getStructureNamesWithTimberListIssues(newState.nodeSets);
      if (csvErrors.length > 0 || structureNames.length > 0) {
        setTimberImportIssueModal({ csvErrors, structureNamesWithIssues: structureNames });
      }

      console.log(`[CSV Import] Created ${added} structures from CSV`);
    } catch (error) {
      console.error('[CSV Import] Error:', error);
      alert('Error importing CSV');
    } finally {
      setImporting(false);
    }
  }, [proposalState, csvParseResult, setProposalState, dealId, proposalData?.latestTimberListPrompt?.fileId]);

  // CSV Import: Reset
  const handleCSVImportReset = useCallback(() => {
    setCsvParseResult(null);
    csvImportSourceRef.current = null;
  }, []);

  /**
   * Apply selected diff back to the proposal:
   *  1. Run the pure apply utility to produce a new proposal state.
   *  2. Push it through `setProposalState` and mark `hasChanges` so the
   *     existing debounced save fires.
   *  3. If the source CSV came from the Hub banner, mark
   *     `timberListAppliedToProposalFileId` to clear the "new file available"
   *     highlight for that file.
   *  4. Re-pull DB unit prices for every touched structure (formerly only
   *     happened when the structure modal opened).
   *  5. Optionally fire the supplier RFQ print as the deleted auto-import
   *     used to do — driven by the modal footer checkbox.
   */
  const handleApplyTimberDiff = useCallback(async (
    selections: TimberDiffSelections,
    opts: { openSupplierRfq: boolean },
  ) => {
    const ctx = updateModal;
    if (!ctx || !proposalState) return;
    const { state: appliedState, touchedNodeSetIds, reorderedOnlyNodeSetIds } = applyProposalTimberDiff(
      proposalState,
      ctx.diff,
      selections,
    );

    isLocalEditRef.current = true;
    setProposalState(appliedState);
    setHasChanges(true);
    setUpdateModal(null);

    // Mark the latest project timber CSV as applied so the banner stops
    // suggesting it. Only meaningful when the source was the Hub file.
    if (ctx.source === 'project' && dealId && ctx.fileMeta?.fileId) {
      const fid = ctx.fileMeta.fileId;
      try {
        await updateDoc(doc(db, 'proposalBuilders', dealId), {
          timberListAppliedToProposalFileId: fid,
        });
        setProposalData(prev => prev ? { ...prev, timberListAppliedToProposalFileId: fid } : null);
      } catch (e) {
        console.error('[handleApplyTimberDiff] mark-applied failed', e);
      }
    }

    // Re-price touched structures from the materials DB (one-shot fetch).
    // Failure here is non-fatal; the structure modal will refill prices on
    // the next open as before.
    try {
      const repriced = await resyncDbPricesForStructures(appliedState, touchedNodeSetIds);
      // Only push back if the repricing actually changed something — comparing
      // by reference is enough because the helper returns a fresh object only
      // when work was done.
      if (repriced !== appliedState) {
        isLocalEditRef.current = true;
        setProposalState(repriced);
        setHasChanges(true);
      }
    } catch (e) {
      console.error('[handleApplyTimberDiff] DB resync failed', e);
    }

    const flashParts: string[] = [];
    if (selections.applyStructureIds.size) {
      flashParts.push(`${selections.applyStructureIds.size} updated`);
    }
    if (selections.addNewGroupNames.size) {
      flashParts.push(`${selections.addNewGroupNames.size} added`);
    }
    if (selections.deleteStructureIds.size) {
      flashParts.push(`${selections.deleteStructureIds.size} removed`);
    }
    if (selections.reorderToCsvOrder) {
      if (reorderedOnlyNodeSetIds.length > 0) {
        flashParts.push(`${reorderedOnlyNodeSetIds.length} reordered`);
      } else if (flashParts.length === 0) {
        // The user asked to reorder but everything was already in CSV order.
        flashParts.push('already in CSV order');
      }
    }
    if (flashParts.length) {
      showFlash(`Timber list applied: ${flashParts.join(', ')}`);
    }

    if (opts.openSupplierRfq) {
      // Slight delay so the freshly-applied state is visible before the print
      // dialog steals focus — matches the timing the deleted auto-import used.
      setTimeout(() => tryOpenSupplierRequestPrint(appliedState), 350);
    }
    // tryOpenSupplierRequestPrint is intentionally omitted from deps to avoid
    // a circular declaration order; it's stable enough for this purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateModal, proposalState, dealId, setProposalState, showFlash]);

  const handleDismissHardwareListBanner = useCallback(async () => {
    if (!dealId || !proposalData?.latestHardwareListPrompt?.fileId) return;
    try {
      await updateDoc(doc(db, 'proposalBuilders', dealId), {
        hardwareListBannerDismissedForFileId: proposalData.latestHardwareListPrompt.fileId,
      });
      setProposalData(prev => prev ? {
        ...prev,
        hardwareListBannerDismissedForFileId: proposalData.latestHardwareListPrompt!.fileId,
      } : null);
    } catch (e) {
      console.error('[Hardware list banner]', e);
      alert('Could not update proposal.');
    }
  }, [dealId, proposalData?.latestHardwareListPrompt?.fileId]);

  const handleImportFromProjectHardwareList = useCallback(async () => {
    const prompt = proposalData?.latestHardwareListPrompt;
    if (!prompt?.fileUrl) return;
    try {
      const res = await fetch(prompt.fileUrl);
      const text = await res.text();
      const result = parseHardwareCsv(text);
      if (result.errors.length && result.items.length === 0) {
        alert(result.errors[0]);
        return;
      }
      setHardwareParseResult(result);
    } catch (e) {
      console.error('[Hardware list import]', e);
      alert('Could not load the hardware list file. Download it from Project Files and try again.');
    }
  }, [proposalData?.latestHardwareListPrompt]);

  const handleApplyHardwareList = useCallback(async (storeyToNodeSetId: Record<string, string>) => {
    if (!proposalState || !hardwareParseResult) return;
    const fileId = proposalData?.latestHardwareListPrompt?.fileId;
    isLocalEditRef.current = true;
    const next = {
      ...proposalState,
      nodeSets: applyHardwareItemsToNodeSets(
        proposalState.nodeSets,
        hardwareParseResult.items,
        storeyToNodeSetId,
        fileId,
      ),
    };
    setProposalState(next);
    setHasChanges(true);
    setHardwareParseResult(null);
    if (dealId && fileId) {
      try {
        await updateDoc(doc(db, 'proposalBuilders', dealId), {
          hardwareListAppliedToProposalFileId: fileId,
        });
        setProposalData(prev => prev ? { ...prev, hardwareListAppliedToProposalFileId: fileId } : null);
      } catch (e) {
        console.error('[Hardware list apply] mark-applied failed', e);
      }
    }
    showFlash('Hardware list applied to mapped structures');
  }, [proposalState, hardwareParseResult, proposalData?.latestHardwareListPrompt?.fileId, dealId, setProposalState, showFlash]);

  // Rename node set
  const handleRenameNodeSet = useCallback((nodeSetId: string, newName: string) => {
    if (!proposalState) return;
    isLocalEditRef.current = true;
    const newState = {
      ...proposalState,
      nodeSets: proposalState.nodeSets.map(ns =>
        ns.id === nodeSetId ? { ...ns, name: newName } : ns
      ),
    };
    setProposalState(newState);
    setHasChanges(true);
  }, [proposalState, setProposalState]);

  // Delete node set
  const handleDeleteNodeSet = useCallback((nodeSetId: string) => {
    if (!proposalState) return;
    if (proposalState.nodeSets.length <= 1) {
      alert('Cannot delete the last node set');
      return;
    }
    isLocalEditRef.current = true;
    const newState = {
      ...proposalState,
      nodeSets: proposalState.nodeSets.filter(ns => ns.id !== nodeSetId),
    };
    setProposalState(newState);
    setHasChanges(true);
  }, [proposalState, setProposalState]);

  // Keep refs updated with latest handlers
  useEffect(() => {
    handleRenameRef.current = handleRenameNodeSet;
    handleDeleteRef.current = handleDeleteNodeSet;
  }, [handleRenameNodeSet, handleDeleteNodeSet]);

  // Update node set data
  const handleUpdateNodeSet = useCallback((nodeSetId: string, updates: Partial<ProposalNodeSet>) => {
    if (isApproved) return;
    if (isSent && !updates.design) return;
    isLocalEditRef.current = true;
    const notes = (updates as any).notes;
    if (notes !== undefined) {
      console.log(`%c[handleUpdateNodeSet] notes="${notes?.slice(-20)}" saveInProgress=${saveInProgressRef.current} hasChanges=${hasChangesRef.current}`, 'color: blue');
    }
    setProposalState(prev => {
      if (!prev) return prev;
      const nextState = {
        ...prev,
        nodeSets: prev.nodeSets.map(ns => {
          if (ns.id !== nodeSetId) return ns;
          const merged: ProposalNodeSet = isSent
            ? { ...ns, design: updates.design ? { ...ns.design, ...updates.design } : ns.design }
            : { ...ns, ...updates };
          if (!isSent) {
            if (updates.install) {
              merged.install = { ...ns.install, ...updates.install };
            }
            if (updates.overhead) {
              merged.overhead = { ...ns.overhead, ...updates.overhead };
            }
            if (updates.materials) {
              merged.materials = { ...ns.materials, ...updates.materials };
            }
            if (updates.production) {
              merged.production = { ...ns.production, ...updates.production };
            }
          }
          return merged;
        }),
      };
      proposalStateRef.current = nextState;
      return nextState;
    });
    setHasChanges(true);
  }, [setProposalState, isSent, isApproved]);

  // Update profit margin
  const handleUpdateProfitMargin = useCallback((margin: number) => {
    if (!proposalState) return;
    isLocalEditRef.current = true;
    const newState = {
      ...proposalState,
      profitMargin: margin,
    };
    setProposalState(newState);
    setHasChanges(true);
  }, [proposalState, setProposalState]);

  // Export timber list to CSV (all structures, no pricing for suppliers)
  const handleExportTimberCSV = useCallback(() => {
    if (!proposalState) return;
    setShowToolsMenu(false);
    downloadTimberSupplierCsv(proposalState, `timber-list-${deal?.title || 'proposal'}`);
  }, [proposalState, deal]);

  /** Opens supplier RFQ print window (sync), or validation warning modal first. */
  const runTimberSupplierPdfExport = useCallback(
    (state: ProposalState) => {
      if (!timberSupplierExportHasContent(state)) {
        window.alert('No timber lines to export. Add timber lists to enabled structures first.');
        return;
      }
      const printWindow = openTimberPrintWindow();
      if (!printWindow) {
        window.alert(TIMBER_SUPPLIER_POPUP_BLOCKED_MSG);
        return;
      }
      void printTimberSupplierToWindow(printWindow, state, deal?.title).catch(err => {
        console.error('Timber PDF export failed:', err);
        window.alert(err instanceof Error ? err.message : 'Failed to open timber PDF.');
        try {
          printWindow.close();
        } catch {
          /* ignore */
        }
      });
    },
    [deal?.title]
  );

  const tryOpenSupplierRequestPrint = useCallback(
    (state: ProposalState) => {
      const names = getStructureNamesWithTimberValidationIssues(state.nodeSets);
      if (names.length > 0) {
        setTimberExportWarningModal({ structureNames: names });
        return;
      }
      runTimberSupplierPdfExport(state);
    },
    [runTimberSupplierPdfExport]
  );

  const handleExportTimberPDF = useCallback(() => {
    if (!proposalState) return;
    setShowToolsMenu(false);
    tryOpenSupplierRequestPrint(proposalState);
  }, [proposalState, tryOpenSupplierRequestPrint]);

  // Export decking supplier request to CSV (per structure, linear feet, no pricing).
  const handleExportDeckingCSV = useCallback(() => {
    if (!proposalState) return;
    setShowToolsMenu(false);
    if (!deckingExportHasContent(proposalState)) {
      window.alert('No decking to export. Add decking to enabled structures first.');
      return;
    }
    downloadDeckingSupplierCsv(proposalState, `decking-request-${deal?.title || 'proposal'}`);
  }, [proposalState, deal]);

  const handleExportDeckingPDF = useCallback(() => {
    if (!proposalState) return;
    setShowToolsMenu(false);
    if (!deckingExportHasContent(proposalState)) {
      window.alert('No decking to export. Add decking to enabled structures first.');
      return;
    }
    const printWindow = openTimberPrintWindow();
    if (!printWindow) {
      window.alert(TIMBER_SUPPLIER_POPUP_BLOCKED_MSG);
      return;
    }
    void printDeckingSupplierToWindow(printWindow, proposalState, deal?.title).catch(err => {
      console.error('Decking PDF export failed:', err);
      window.alert(err instanceof Error ? err.message : 'Failed to open decking PDF.');
      try {
        printWindow.close();
      } catch {
        /* ignore */
      }
    });
  }, [proposalState, deal]);

  const timberProjectForDeal = React.useMemo(
    () => (dealId ? timberProjects.find(p => p.dealId === dealId) ?? null : null),
    [dealId, timberProjects],
  );

  const handleStageForSupplierRfq = useCallback(async () => {
    if (!dealId || !proposalState || stagingRfqBusy) return;
    if (!timberSupplierExportHasContent(proposalState)) {
      window.alert('No timber lines to stage. Add timber lists to enabled structures first.');
      return;
    }
    const issueNames = getStructureNamesWithTimberValidationIssues(proposalState.nodeSets);
    if (issueNames.length > 0) {
      window.alert(
        `Fix timber validation issues before staging for suppliers: ${issueNames.join(', ')}`,
      );
      return;
    }
    setStagingRfqBusy(true);
    try {
      const saved = await saveProposalInternal(proposalState);
      if (!saved) {
        window.alert('Could not save the proposal. Fix any issues and try again.');
        return;
      }
      const result = await pushProposalTimberToRfqStaging({
        dealId,
        timberProjectId: timberProjectForDeal?.id,
        timberProjectName: timberProjectForDeal?.name,
        dealTitle: deal?.title,
      });
      showFlash(
        `Staged as RFQ v${result.releaseVersionIndex} — open Project Hub → Suppliers to publish.`,
      );
      if (projectIdForFiles) {
        navigate(`/project-hub/${projectIdForFiles}?tab=suppliers`);
      }
    } catch (err) {
      console.error(err);
      window.alert(err instanceof Error ? err.message : 'Could not stage timber for supplier RFQ.');
    } finally {
      setStagingRfqBusy(false);
    }
  }, [
    dealId,
    proposalState,
    stagingRfqBusy,
    timberProjectForDeal,
    deal?.title,
    projectIdForFiles,
    navigate,
    showFlash,
  ]);

  /**
   * Generate the Internal Review PDF in memory and open a preview popup.
   * The user can review on screen and *choose* to download — historically
   * this fired a download every click, which was wasteful when the staff
   * member just wanted to skim the numbers.
   */
  const handleInternalReview = useCallback(async () => {
    if (!proposalState || !deal) return;
    setShowToolsMenu(false);
    setInternalReviewLoading(true);

    try {
      const reviewData: InternalReviewData = {
        clientName: deal.contactName || deal.company || 'Unknown Client',
        projectTitle: deal.title || 'Untitled Project',
        preparedBy: user?.name || user?.email,
        proposalDate: new Date(),
        dealId,
        status: proposalData?.status,
        nodeSets: proposalState.nodeSets,
        profitMargin: proposalState.profitMargin,
        globalCosts: proposalState.globalCosts,
        costingModel: proposalState.costingModel,
        isKitProposal: proposalState.isKitProposal,
        activeSpecies: proposalState.activeSpecies,
        sourceKitName: proposalState.sourceKitName,
        designEstimating: proposalState.designEstimating,
        rates,
        joineryPeople: crmUsers.reduce<ReviewJoineryPerson[]>((people, row) => {
          const name = (row.displayName || row.email || '').trim();
          if (!row.uid || !name) return people;
          const title = (row.title || '').trim();
          people.push({ userId: row.uid, name, ...(title ? { title } : {}) });
          return people;
        }, []),
      };

      const blob = await pdf(
        <InternalReviewPdfDocument data={reviewData} />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const filename = `INTERNAL_REVIEW_${deal?.title?.replace(/\s+/g, '_') || 'proposal'}_${new Date().toISOString().split('T')[0]}.pdf`;
      // Revoke any previous URL before swapping in the new one.
      setInternalReviewPdf(prev => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return { url, filename };
      });
    } catch (error) {
      console.error('Error generating internal review PDF:', error);
      alert('Failed to generate internal review PDF. Check console for details.');
    } finally {
      setInternalReviewLoading(false);
    }
  }, [proposalState, deal, dealId, user, proposalData, rates, crmUsers]);

  /** Trigger a browser download of the currently-previewed Internal Review PDF. */
  const handleDownloadInternalReview = useCallback(() => {
    if (!internalReviewPdf) return;
    const a = document.createElement('a');
    a.href = internalReviewPdf.url;
    a.download = internalReviewPdf.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [internalReviewPdf]);

  /** Close the preview and revoke the blob URL so it doesn't leak memory. */
  const handleCloseInternalReview = useCallback(() => {
    setInternalReviewPdf(prev => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  /**
   * Debug Pricing (staff only) — generates the comprehensive pricing-debug PDF
   * that diffs the stored `billablePrices` snapshot against a live recompute,
   * plus a per-bucket rounding audit. Used to pin down review-vs-client-view drift.
   */
  const handleDebugPricing = useCallback(async () => {
    if (!proposalState) return;
    setShowToolsMenu(false);
    setDebugPricingLoading(true);

    try {
      const debugData = generatePricingDebugData(
        {
          ...proposalState,
          id: dealId,
          displayTotal: (proposalData as unknown as { displayTotal?: number })?.displayTotal,
          updatedAt: proposalData?.updatedAt,
          sentAt: proposalData?.sentAt,
          createdAt: proposalData?.createdAt,
          pricingLocked: proposalData?.pricingLocked,
          sourceKitId: proposalState.sourceKitId,
          sourceKitName: proposalState.sourceKitName,
          status: proposalData?.status,
          slug: proposalData?.slug,
          billableTotal: proposalData?.billableTotal,
          rawCostTotal: proposalData?.rawCostTotal,
          designEstimating: proposalState.designEstimating,
        },
        rates || undefined,
      );

      const blob = await pdf(
        <PricingDebugPdfDocument
          debugData={debugData}
          clientName={deal?.contactName}
          proposalName={deal?.title}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PRICING_DEBUG_${deal?.title?.replace(/\s+/g, '_') || 'proposal'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to generate pricing debug PDF:', error);
      alert('Failed to generate pricing debug PDF. Check console for details.');
    } finally {
      setDebugPricingLoading(false);
    }
  }, [proposalState, proposalData, deal, dealId, rates]);

  // Cleanup any open blob URL on unmount so navigating away doesn't leak.
  useEffect(() => {
    return () => {
      if (internalReviewPdf?.url) URL.revokeObjectURL(internalReviewPdf.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save proposal - uses a ref to get current state to avoid stale closures
  const proposalStateRef = useRef(proposalState);
  useEffect(() => {
    proposalStateRef.current = proposalState;
  }, [proposalState]);

  const [catalogTimber, setCatalogTimber] = useState<TimberItem[]>([]);
  const [catalogHardware, setCatalogHardware] = useState<HardwareItem[]>([]);
  const catalogNodeSetIds = (proposalState?.nodeSets || []).map(ns => ns.id).join(',');

  useEffect(() => {
    if (isSent || !dealId) return;
    const unsubTimber = subscribeToTimberItems(
      setCatalogTimber,
      (error) => console.error('[ProposalBuilder] Timber catalog error:', error),
    );
    const unsubHardware = subscribeToHardwareItems(
      setCatalogHardware,
      (error) => console.error('[ProposalBuilder] Hardware catalog error:', error),
    );
    return () => {
      unsubTimber();
      unsubHardware();
    };
  }, [isSent, dealId]);

  // Unsent proposals stay live with the materials database (post bases, timber, decking).
  useEffect(() => {
    if (isSent || !dealId) return;
    if (catalogTimber.length === 0 && catalogHardware.length === 0) return;
    const state = proposalStateRef.current;
    if (!state?.nodeSets.length) return;
    const { nodeSets, changed } = syncProposalNodeSetsCatalogPrices(
      state.nodeSets,
      catalogTimber,
      catalogHardware,
    );
    if (!changed) return;
    isLocalEditRef.current = true;
    setProposalState(prev => (prev ? { ...prev, nodeSets } : prev));
    setHasChanges(true);
  }, [catalogTimber, catalogHardware, catalogNodeSetIds, isSent, dealId, setProposalState]);

  // Auto-save with debounce when changes are made
  useEffect(() => {
    if (!hasChanges || !dealId) return;
    if (isSignedCommercial) return;

    // If save is already in progress, mark pending and don't start new timer
    if (saveInProgressRef.current) {
      pendingSaveRef.current = true;
      return;
    }

    const timeoutId = setTimeout(() => {
      console.log(`%c[AutoSave] Debounce fired — calling saveProposalInternal`, 'color: orange');
      saveProposalInternal();
    }, 500); // 500ms debounce — saves quickly after each field change

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanges, proposalState, dealId]);

  // Timber CSV intake is explicit via Tools → Timber Input (and structure-level import).

  const saveProposalInternal = async (
    stateOverride?: ProposalState,
  ): Promise<ProposalBuilderData | undefined> => {
    // Prevent concurrent saves
    if (saveInProgressRef.current) {
      pendingSaveRef.current = true;
      return undefined;
    }
    saveInProgressRef.current = true;
    pendingSaveRef.current = false;

    const state = stateOverride || proposalStateRef.current;

    if (!dealId || !deal || !state) return undefined;

    const signedCommercial =
      proposalData?.status === 'approved' && !!proposalData?.signature;

    let workingNodeSets = state.nodeSets;
    if (!signedCommercial) {
      if (usesGlobalStructureCosting(state)) {
        workingNodeSets = zeroNodeFreightAndOverheadForGlobalCosting(workingNodeSets);
      }
      if (rates) {
        workingNodeSets = syncNodeSetsForProjectOverhead(
          workingNodeSets,
          state.globalCosts,
          rates,
        );
      }
    }
    const workingState =
      workingNodeSets !== state.nodeSets ? { ...state, nodeSets: workingNodeSets } : state;

    const firstNotes = workingState.nodeSets[0]?.notes;
    console.log(`%c[SAVE START] notes="${firstNotes?.slice(-20)}" pendingSave=${pendingSaveRef.current}`, 'color: orange; font-weight: bold');

    setSaving(true);
    try {
      // Fetch fresh deal data from Firestore to get current values including company
      const dealRef = doc(db, 'enhancedDeals', dealId);
      const dealSnap = await getDoc(dealRef);
      const freshDealData = dealSnap.exists() ? dealSnap.data() : null;
      const currentCompany = freshDealData?.company || deal.company || '';

      // Only include enabled structures in pricing calculations
      const enabledNodeSets = workingState.nodeSets.filter(ns => ns.enabled !== false);

      const globalCostsTotal = workingState.globalCosts?.totalGlobalCosts ?? 0;
      const overheadPct = getEffectiveProjectOverheadPercent(workingState.globalCosts);
      const discountAmount = getProposalDiscountAmount(workingState.discount);

      // Calculate totals (only enabled structures), including global costs and optional project-overhead %
      let stampedEstimating = rates
        ? stampDesignEstimatingTotal(workingState.designEstimating, rates, proposalData?.status)
        : workingState.designEstimating;
      if (
        rates &&
        stampedEstimating &&
        isSent &&
        !stampedEstimating.unlocked &&
        !stampedEstimating.locked
      ) {
        stampedEstimating = lockDesignEstimating(stampedEstimating, rates);
      }
      const totals = calculateTotals(enabledNodeSets, workingState.profitMargin, {
        globalCostsTotal,
        overheadPercentOfProject: overheadPct,
        discountAmount,
        designEstimating: stampedEstimating,
        designEstimatingRates: rates,
        proposalStatus: proposalData?.status,
      });

      // Distribute global costs and project overhead into timber kit billable (per structure)
      // NOTE: This does NOT modify the persisted nodeSet overhead — only used for billablePrices
      const enabledCount = enabledNodeSets.length || 1; // avoid division by zero
      const globalShareEligibleCount = countGlobalCostShareEligible(enabledNodeSets);
      if (globalCostsTotal > 0 && globalShareEligibleCount === 0) {
        alert(
          'When global project costs are greater than zero, at least one enabled structure must receive the global cost split. Turn off “Exclude from global cost split” on one structure.'
        );
        setSaving(false);
        saveInProgressRef.current = false;
        return undefined;
      }
      const globalShareCount = globalShareEligibleCount || 1;
      const globalAddonPerStructure = globalCostsTotal > 0 ? globalCostsTotal / globalShareCount : 0;
      const projectOverheadPerStructure = (totals.projectOverheadAmount ?? 0) / enabledCount;

      const preserveSignedCommercialNodeSets = () =>
        workingState.nodeSets.map(ns => {
          const snapshot = proposalData?.nodeSets?.find(s => s.id === ns.id);
          if (!snapshot) return ns;
          return {
            ...ns,
            billablePrices: snapshot.billablePrices ?? ns.billablePrices,
            install: snapshot.install ?? ns.install,
            overhead: snapshot.overhead ?? ns.overhead,
          };
        });

      // Pre-calculate billable prices for each nodeSet (hides profit margin from clients)
      const nodeSetsWithBillablePrices = signedCommercial
        ? preserveSignedCommercialNodeSets()
        : workingState.nodeSets.map(ns => {
        // Disabled structures get zero billable prices
        if (ns.enabled === false) {
          return {
            ...ns,
            billablePrices: { design: 0, timberKit: 0, install: 0, travel: 0, subtotal: 0 }
          };
        }
        const qty = ns.quantity || 1;
        const receivesGlobalShare = !ns.excludeFromGlobalPrices;
        // Calculate billable prices with profit margin applied
        const designBillable = Math.round(applyProfitMargin(ns.design.total, state.profitMargin) * qty);
        // Global cost addon is a flat per-structure amount — NOT multiplied by qty (only structures that share global costs)
        const timberKitBillable = Math.round(
          applyProfitMargin(ns.materials.total + ns.production.total + ns.freight.total + ns.overhead.total, state.profitMargin) * qty
          + applyProfitMargin(receivesGlobalShare ? globalAddonPerStructure : 0, state.profitMargin)
          + applyProfitMargin(projectOverheadPerStructure, state.profitMargin)
        );
        const installBillable = ns.excludeInstall
          ? 0
          : Math.round(applyProfitMargin(
              ns.install.total + (ns.install.overheadTotal || 0),
              state.profitMargin
            ) * qty);
        const travelBillable = ns.excludeInstall
          ? 0
          : Math.round(applyProfitMargin(ns.travel.total, state.profitMargin) * qty);

        // Calculate species options if available
        const speciesOptions: Record<string, number> = {};
        if (ns.materials.alternateSpecies && ns.materials.alternateSpecies.length > 0) {
          // Add default species price
          const defaultSpeciesName = ns.materials.timberList[0]?.species || 'Default';
          speciesOptions[defaultSpeciesName] = timberKitBillable;

          // Add alternate species prices
          ns.materials.alternateSpecies.forEach(alt => {
            speciesOptions[alt.speciesName] = applySpeciesTimberKitBillableDelta(
              ns,
              alt.speciesName,
              timberKitBillable,
              state.profitMargin,
              qty,
            );
          });
        }

        return {
          ...ns,
          billablePrices: {
            design: designBillable,
            timberKit: timberKitBillable,
            install: installBillable,
            travel: travelBillable,
            subtotal: designBillable + timberKitBillable + installBillable + travelBillable,
            speciesOptions: Object.keys(speciesOptions).length > 0 ? speciesOptions : undefined,
          }
        };
      });

      // Use nodeSets with billable prices
      const updatedNodeSets = nodeSetsWithBillablePrices;

      const proposalRef = doc(db, 'proposalBuilders', dealId);

      // Project name on the proposal doc — prefer the canonical descriptor
      // (set by the naming wizard / migration). Fall back to the legacy
      // " - " title split ONLY for un-migrated deals so we don't regress a
      // freshly-renamed project on the very next save. As a final fallback
      // use the raw title — same behavior as before for un-migratable rows.
      const projectName = (deal.descriptor && deal.descriptor.trim())
        || parseLegacyTitle(deal.title || '').descriptor
        || (deal.title || '');

      // Format location from address or use location field
      const locationStr = deal.address
        ? `${deal.address.street || ''} ${deal.address.city || ''}, ${deal.address.state || ''} ${deal.address.zipCode || ''}`.trim()
        : deal.location || '';

      // Slug stability rule (Stage 4 fix):
      //   • If the proposal already has a slug AND `slugLocked` is true, NEVER
      //     regenerate. Once a client has been given a public URL we never
      //     break it — even if the project is renamed.
      //   • If the proposal has a slug but `slugLocked` is unset/false, we
      //     also keep it (a slug exists, the client may have it). The lock
      //     flips to `true` the first time the proposal is sent or signed.
      //   • Only generate a fresh slug when none exists yet (first save).
      let cleanSlug: string;
      if (proposalData?.slug) {
        cleanSlug = proposalData.slug;
      } else if (state.isKitProposal && state.sourceKitName) {
        // Kit proposal: clientName-kitName
        const clientPart = deal.contactName || deal.title || 'client';
        cleanSlug = `${clientPart}-${state.sourceKitName}`
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
      } else {
        // Custom proposal: contactName-projectName (one-time, on first save)
        const slugBase = projectName || deal.title;
        const contactNameOrTitle = deal.contactName || deal.title || 'client';
        cleanSlug = `${contactNameOrTitle}-${slugBase}`
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
      }

      // Client name resolution: prefer the linked Lead's full name (matches
      // the server-side fan-out in `onEnhancedDealRenamed`), then the
      // deal's contactName, then the legacy title's "Last name" fragment as
      // a last resort. NEVER fall through to the raw title — it can carry
      // the project number / descriptor which would corrupt every place
      // that reads `clientName` (engagement letter, T&Cs, email subjects).
      const linkedLead = deal.leadId ? leads.find(l => l.id === deal.leadId) : undefined;
      const composedFromLead = linkedLead
        ? [linkedLead.firstName, linkedLead.lastName].map(s => (s || '').trim()).filter(Boolean).join(' ').trim()
        : '';
      const clientNameForProposal =
        composedFromLead
        || (deal.contactName || '').trim()
        || parseLegacyTitle(deal.title || '').label
        || (deal.title || '');

      const proposalDoc: ProposalBuilderData = {
        id: dealId,
        dealId,
        clientName: clientNameForProposal,
        leadCompany: currentCompany || '',
        projectName: projectName || '',
        projectNumber: (deal.projectNumber || proposalData?.projectNumber || '').trim() || undefined,
        clientLabel: (deal.clientLabel || proposalData?.clientLabel || '').trim() || undefined,
        descriptor: (deal.descriptor || proposalData?.descriptor || '').trim() || undefined,
        location: locationStr || '',
        slug: cleanSlug,
        slugLocked: proposalData?.slugLocked || false,
        accessPin: proposalData?.accessPin || generatePin(),
        nodeSets: updatedNodeSets,
        profitMargin: state.profitMargin,
        installOptional: state.installOptional || false,
        designIncludedInKit: resolveDesignIncludedInKit(state.designIncludedInKit),
        isRomPricing: state.isRomPricing || false,
        projectSquareFootage: (state.projectSquareFootage && state.projectSquareFootage > 0
          ? Math.round(state.projectSquareFootage)
          : deleteField()) as unknown as number | undefined,
        phasingEnabled: state.phasingEnabled ?? proposalData?.phasingEnabled ?? false,
        phases: state.phases ?? proposalData?.phases,
        status: proposalData?.status || 'draft',
        checklist: state.checklist,
        ...totals,
        createdAt: proposalData?.createdAt || new Date(),
        updatedAt: new Date(),
        createdBy: proposalData?.createdBy || 'system',
        // Kit proposal tracking
        isKitProposal: state.isKitProposal || proposalData?.isKitProposal,
        sourceKitId: state.sourceKitId || proposalData?.sourceKitId,
        sourceKitName: state.sourceKitName || proposalData?.sourceKitName,
        isCustomized: state.isCustomized || proposalData?.isCustomized,
        offeredSpecies: state.offeredSpecies || proposalData?.offeredSpecies,
        activeSpecies: state.activeSpecies || proposalData?.activeSpecies,
        kitPricing: state.kitPricing || proposalData?.kitPricing,
        globalCosts: state.globalCosts,
        costingModel: state.costingModel || proposalData?.costingModel,
        discount: workingState.discount ?? proposalData?.discount,
        designEstimating: stampedEstimating,
        latestTimberListPrompt: proposalData?.latestTimberListPrompt,
        timberListBannerDismissedForFileId: proposalData?.timberListBannerDismissedForFileId,
        timberListAppliedToProposalFileId: proposalData?.timberListAppliedToProposalFileId,
        latestHardwareListPrompt: proposalData?.latestHardwareListPrompt,
        hardwareListBannerDismissedForFileId: proposalData?.hardwareListBannerDismissedForFileId,
        hardwareListAppliedToProposalFileId: proposalData?.hardwareListAppliedToProposalFileId,
      };

      if (signedCommercial && proposalData) {
        Object.assign(proposalDoc, {
          designTotal: proposalData.designTotal,
          timberKitTotal: proposalData.timberKitTotal,
          installTotal: proposalData.installTotal,
          billableTotal: proposalData.billableTotal,
          rawCostTotal: proposalData.rawCostTotal,
          grossProfit: proposalData.grossProfit,
          projectOverheadAmount: proposalData.projectOverheadAmount,
          structureRawCostTotal: proposalData.structureRawCostTotal,
          grandTotal: proposalData.grandTotal,
          speciesPricing: proposalData.speciesPricing,
          selectedSpecies: proposalData.selectedSpecies,
          installSelection: proposalData.installSelection,
          structureSelections: proposalData.structureSelections,
          discount: proposalData.discount,
        });
      }

      // List/deal cards use the default Choose one pool (same as footer / public proposal).
      const displaySubtotal = Math.round(
        computeExclusiveStaffEconomics(
          updatedNodeSets,
          state.profitMargin,
          state.globalCosts,
          {
            isKitProposal: state.isKitProposal,
            kitPricing: state.kitPricing,
            activeSpecies:
              state.activeSpecies || state.offeredSpecies?.[0] || state.kitPricing?.[0]?.speciesName,
            discountAmount,
            designEstimating: stampedEstimating,
            designEstimatingRates: rates,
            proposalStatus: proposalData?.status,
          },
        ).defaultEconomics.billableTotal,
      );
      // Bonding pass-through rides on top so list rows / deal value match the client page.
      const displayTotal =
        displaySubtotal +
        computeBondingAmount(displaySubtotal, effectiveBondingPercent(state.globalCosts));

      // Add displayTotal to proposal document
      if (signedCommercial && proposalData) {
        const signedDisplay = getSignedCommercialListTotal(proposalData);
        (proposalDoc as any).displayTotal =
          signedDisplay ?? (proposalData as { displayTotal?: number }).displayTotal ?? displayTotal;
      } else {
        (proposalDoc as any).displayTotal = displayTotal;
      }

      // Deep filter to remove undefined values recursively (Firestore requirement)
      const cleanProposalDoc = removeUndefinedDeep(proposalDoc);

      // Record save time to ignore the onSnapshot callback triggered by our own write
      lastSaveTimeRef.current = Date.now();

      await setDoc(proposalRef, cleanProposalDoc, { merge: true });

      if (opsReviewMetaRef.current?.status === 'approved') {
        await revokeOpsReviewApproval(dealId);
      }

      // Update deal value to match displayTotal (never overwrite signed commercial value)
      if (dealId && displayTotal > 0 && !signedCommercial) {
        await updateEnhancedDeal(dealId, { value: displayTotal });
      }

      // Merge only billablePrices into the CURRENT state (not the stale snapshot)
      // This preserves any edits the user typed while the save was in progress
      const freshState = proposalStateRef.current;
      if (freshState) {
        const savedNodeMap = new Map(updatedNodeSets.map(ns => [ns.id, ns]));
        const mergedNodeSets = freshState.nodeSets.map(ns => {
          const saved = savedNodeMap.get(ns.id);
          if (!saved) return ns;
          return {
            ...ns,
            billablePrices: saved.billablePrices ?? ns.billablePrices,
            overhead: saved.overhead ?? ns.overhead,
            freight: saved.freight ?? ns.freight,
            // Keep user-edited install fields (rates, days, flags); only apply save-time totals.
            install: {
              ...ns.install,
              total: saved.install?.total ?? ns.install.total,
              overheadTotal: saved.install?.overheadTotal ?? ns.install.overheadTotal,
            },
          };
        });
        setProposalState({
          ...freshState,
          nodeSets: mergedNodeSets,
        });
        console.log(`%c[SAVE END] Merged billablePrices into fresh state (preserved in-flight edits)`, 'color: green; font-weight: bold');
      }

      // Also update proposalData so slug/accessPin are immediately available for preview
      setProposalData(proposalDoc);

      // Only clear hasChanges if no pending edits arrived during the save
      if (!pendingSaveRef.current) {
        setHasChanges(false);
      }

      // Reset edit flag after save
      isLocalEditRef.current = false;

      return cleanProposalDoc as ProposalBuilderData;
    } catch (error) {
      console.error('Error saving proposal:', error);
      alert('Failed to save proposal');
      return undefined;
    } finally {
      setSaving(false);
      saveInProgressRef.current = false;

      // If there were pending changes during save, trigger another save
      if (pendingSaveRef.current && hasChangesRef.current) {
        console.log('[AutoSave] Pending changes detected, triggering follow-up save...');
        pendingSaveRef.current = false;
        // Small delay to let state settle
        setTimeout(() => saveProposalInternal(), 100);
      }
    }
  };



  // Generate 4-digit PIN
  const generatePin = () => {
    return Math.floor(1000 + Math.random() * 9000).toString();
  };

  // Alias for manual save (Ctrl+S)
  const saveProposal = saveProposalInternal;

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        saveProposal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  if (loading) {
    return (
      <div className="proposal-builder-page">
        <AppHeader />
        <div className="proposal-builder-loading">
          <div className="loading-spinner" />
          <p>Loading proposal builder...</p>
        </div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="proposal-builder-page">
        <AppHeader />
        <div className="proposal-builder-error">
          <p>Deal not found</p>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  // Preview uses the default Choose one pool (same as footer / public proposal).
  const previewTotals = proposalState
    ? calculateTotals(
        nodeSetsForExclusiveSelection(proposalState.nodeSets),
        proposalState.profitMargin,
        {
          globalCostsTotal: proposalState.globalCosts?.totalGlobalCosts ?? 0,
          overheadPercentOfProject: getEffectiveProjectOverheadPercent(proposalState.globalCosts),
          discountAmount: getProposalDiscountAmount(proposalState.discount),
          designEstimating: proposalState.designEstimating,
          designEstimatingRates: rates,
          proposalStatus: proposalData?.status,
        },
      )
    : null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const hardwarePrompt = proposalData?.latestHardwareListPrompt;
  const dismissedHardwareId = proposalData?.hardwareListBannerDismissedForFileId ?? null;
  const appliedHardwareFileMatches = proposalData?.hardwareListAppliedToProposalFileId === hardwarePrompt?.fileId;
  const showHardwareListBanner =
    !!hardwarePrompt &&
    hardwarePrompt.fileId !== dismissedHardwareId &&
    !proposalState?.isKitProposal;
  const hardwareBannerCanEdit = showHardwareListBanner && !isSent;
  const showImportHardwareFromProjectFile = hardwareBannerCanEdit && !appliedHardwareFileMatches;

  const hardwarePromptUploadedLabel = (() => {
    if (!hardwarePrompt?.uploadedAt) return null;
    const u = hardwarePrompt.uploadedAt as Date | { toDate?: () => Date };
    const d = typeof (u as { toDate?: () => Date }).toDate === 'function'
      ? (u as { toDate: () => Date }).toDate()
      : u instanceof Date ? u : null;
    return d ? d.toLocaleString() : null;
  })();

  return (
    <div className="proposal-builder-page">
      <AppHeader />


      {/* Preview Modal */}
      {showPreview && proposalState && previewTotals && (
        <div className="preview-modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h2><FileText size={20} /> Proposal Preview</h2>
              <button className="close-preview-btn" onClick={() => setShowPreview(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="preview-modal-content">
              <div className="preview-deal-info">
                <h3>{deal.title}</h3>
                <p>{deal.contactName}</p>
              </div>

              <div className="preview-line-items">
                <h4>Line Items</h4>
                <div className="preview-item">
                  <span>Design Services</span>
                  <span>{formatCurrency(applyProfitMargin(previewTotals.designTotal, proposalState.profitMargin))}</span>
                </div>
                <div className="preview-item">
                  <span>Timber Kit</span>
                  <span>{formatCurrency(applyProfitMargin(previewTotals.timberKitTotal, proposalState.profitMargin))}</span>
                </div>
                <div className="preview-item">
                  <span>Installation</span>
                  <span>{formatCurrency(applyProfitMargin(previewTotals.installTotal, proposalState.profitMargin))}</span>
                </div>
                <div className="preview-total">
                  <span>Total</span>
                  <span>{formatCurrency(previewTotals.billableTotal)}</span>
                </div>
              </div>

              <div className="preview-node-sets">
                <h4>Included Structures ({proposalState.nodeSets.filter(ns => ns.enabled !== false).length})</h4>
                {proposalState.nodeSets.filter(ns => ns.enabled !== false).map(ns => (
                  <div key={ns.id} className="preview-node-set">
                    <span>{ns.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="preview-modal-footer">
              {proposalData?.slug && (
                <button
                  className="view-public-btn"
                  onClick={() => window.open(`/proposal/${proposalData.slug}`, '_blank')}
                >
                  <ExternalLink size={16} />
                  View Public Proposal
                </button>
              )}
              <button className="close-btn" onClick={() => setShowPreview(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Kit Proposal Wizard */}
      {showKitWizard && dealId && (
        <KitProposalWizard
          dealId={dealId}
          rates={rates}
          onComplete={handleKitWizardComplete}
          onCancel={() => navigate(-1)}
        />
      )}

      {/* Main Builder */}
      {checklistCompleted && (
        <div className={`proposal-builder-container${compareSession ? ' is-comparing' : ''}`}>
          {/* Header */}
          <div className="builder-header">
            <button className="back-btn" onClick={() => deal?.leadId ? navigate(`/client/${deal.leadId}`) : navigate('/proposals')} title={deal?.leadId ? 'Back to Client Portal' : 'Back to Proposals'}>
              <ChevronLeft size={20} />
            </button>
            <div className="builder-title">
              <div className="builder-title-row">
                {proposalState?.isKitProposal && !proposalState?.isCustomized ? (
                  <h1 className="kit-proposal-title">
                    <span className="kit-label">Kit Proposal</span>
                    {proposalState.sourceKitName}
                  </h1>
                ) : (
                  <h1>Proposal Builder</h1>
                )}
                {proposalData?.status === 'sent' && <span className="status-badge sent">Sent</span>}
                {proposalData?.status === 'approved' && <span className="status-badge approved">Approved</span>}
              </div>
              <span className="deal-name">{deal.title}</span>
            </div>
            <div className="builder-status">
              {/* Global Costs — project overhead %, estimating, and dollar lines */}
              {(() => {
                const estimatingAmount = rates
                  ? estimatingRaw(proposalState?.designEstimating, rates, proposalData?.status)
                  : 0;
                const bondingPct = effectiveBondingPercent(proposalState?.globalCosts);
                const hasCosts =
                  !!proposalState?.globalCosts?.totalGlobalCosts ||
                  estimatingAmount > 0 ||
                  getEffectiveProjectOverheadPercent(proposalState?.globalCosts) > 0 ||
                  bondingPct > 0;
                const baseLabel = proposalState?.globalCosts?.totalGlobalCosts
                  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(proposalState.globalCosts.totalGlobalCosts)
                  : estimatingAmount > 0
                    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(estimatingAmount)
                  : getEffectiveProjectOverheadPercent(proposalState?.globalCosts) > 0
                    ? `${getEffectiveProjectOverheadPercent(proposalState.globalCosts)}% overhead`
                    : null;
                const totalLabel = bondingPct > 0
                  ? `${baseLabel ? `${baseLabel} · ` : ''}Bonding ${formatBondingPercent(bondingPct)}`
                  : baseLabel;
                return (
                  <button
                    className="pricing-lock-btn"
                    onClick={() => setShowGlobalCosts(true)}
                    title={totalLabel ? `Global project costs · ${totalLabel}` : 'Manage global project costs'}
                    style={hasCosts ? { borderColor: 'var(--color-success)', background: 'var(--color-primary-subtle)' } : {}}
                  >
                    <Briefcase size={14} />
                    Global Costs
                    {bondingPct > 0 && <span className="pricing-lock-badge">Bonding {formatBondingPercent(bondingPct)}</span>}
                  </button>
                );
              })()}

              {/* Promotional Discount Button */}
              {(() => {
                const activeDiscount = getProposalDiscountAmount(proposalState?.discount);
                const discountLabel =
                  activeDiscount > 0
                    ? `${proposalState?.discount?.label || 'Discount'} · −${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(activeDiscount)}`
                    : null;
                return (
                  <button
                    className="pricing-lock-btn"
                    onClick={() => setShowDiscountModal(true)}
                    title={discountLabel ?? 'Promotional discount (flat dollars off the client total)'}
                    style={activeDiscount > 0 ? { borderColor: 'var(--color-success)', background: 'var(--color-primary-subtle)' } : {}}
                  >
                    <BadgePercent size={14} />
                    Discount
                  </button>
                );
              })()}

              <div className="pb-compare-wrap">
                <button
                  ref={compareBtnRef}
                  type="button"
                  className={`pricing-lock-btn pb-compare-btn${compareSession ? ' active' : ''}`}
                  onClick={() => {
                    if (compareSession) {
                      setCompareSession(null);
                      setShowComparePicker(false);
                      return;
                    }
                    setShowComparePicker(open => !open);
                  }}
                  title={compareSession ? 'Exit structure compare' : 'Compare two structures'}
                >
                  <Columns2 size={14} />
                  Compare
                </button>
                {showComparePicker && proposalState && dealId && (
                  <StructureComparePicker
                    currentDealId={dealId}
                    nodeSets={proposalState.nodeSets}
                    defaultLeftId={expandedCardId}
                    deals={enhancedDeals}
                    anchorRef={compareBtnRef}
                    onClose={() => setShowComparePicker(false)}
                    onApply={(session) => {
                      setCompareSession(session);
                      setShowComparePicker(false);
                      setExpandedCardId(null);
                    }}
                    onComparePages={() => {
                      setShowComparePicker(false);
                      const left = proposalData?.id || dealId;
                      navigate(left ? `/proposals?compare=1&left=${encodeURIComponent(left)}` : '/proposals?compare=1');
                    }}
                  />
                )}
              </div>

              {/* Kit Mode Indicator */}
              {proposalState?.isKitProposal && !proposalState?.isCustomized && (
                <span className="status-badge kit-mode" title={`Kit: ${proposalState?.sourceKitName}`}>
                  Kit Proposal
                </span>
              )}
            </div>
            {/* Header Actions */}
            <div className="header-actions">
              {projectIdForFiles && (
                <button
                  type="button"
                  className="pb-hub-files-btn"
                  onClick={openProjectHubTable}
                  title="Open Project Hub table for this project (P)"
                  aria-label="Open Project Hub table for this project"
                  aria-keyshortcuts="P"
                >
                  <LayoutList size={14} />
                  Project Hub
                </button>
              )}
              {showOpsReviewBtn && (
                <button
                  type="button"
                  className={`por-header-btn por-ops-review-summary-btn ${
                    opsReviewMeta?.status === 'pending'
                      ? 'is-pending'
                      : opsReviewMeta?.status === 'approved'
                        ? 'is-approved'
                        : ''
                  }`}
                  onClick={() => setShowOpsCommentsPanel(true)}
                  title="OPS review — request, comments, and approval"
                >
                  <ShieldCheck size={14} />
                  {opsReviewSummaryLabel}
                  {opsReviewCommentCount > 0 && (
                    <span className="por-count">{opsReviewCommentCount}</span>
                  )}
                </button>
              )}
              {isAdminBuilderUser && dealId && proposalData && (
                <button
                  type="button"
                  className={`por-header-btn pb-agent-review-btn ${isReviewRunning ? 'is-pending' : hasReviewResult ? 'is-approved' : ''}`}
                  onClick={() => void handleAgentReview()}
                  disabled={proposalReviewBusy || isReviewRunning}
                  title="Send this proposal to the bidding agent for a pre-send review (win likelihood, economics, ways to improve odds, conformance)"
                  aria-label="Agent review of this proposal"
                >
                  {isReviewRunning || proposalReviewBusy ? <Loader2 size={14} className="pb-spin" /> : <Bot size={14} />}
                  {isReviewRunning ? 'Reviewing…' : hasReviewResult ? 'Reviewed' : 'Agent Review'}
                </button>
              )}
              {latestHtmlModel && (
                <button
                  type="button"
                  className="pb-3d-quick-btn"
                  onClick={() => setShowHtmlModelPopup(true)}
                  title={`Open latest 3D model: ${latestHtmlModel.name}${latestHtmlModel.versionLabel ? ` (${latestHtmlModel.versionLabel})` : ''}`}
                  aria-label="Open latest 3D model"
                >
                  <Box size={14} />
                </button>
              )}
              {isStaffBuilderUser && dealId && (
                <button
                  type="button"
                  className="pb-3d-quick-btn"
                  onClick={() => void handleStageForSupplierRfq()}
                  disabled={stagingRfqBusy || !proposalState || isSent}
                  title="Save a proposal snapshot and stage timber CSV for supplier RFQ"
                  aria-label="Stage for supplier RFQ"
                >
                  {stagingRfqBusy ? <Loader2 size={14} className="pb-spin" /> : <Send size={14} />}
                </button>
              )}
              {isStaffBuilderUser && dealId && (
                <button
                  type="button"
                  className="pb-3d-quick-btn"
                  onClick={() => setShowVersionHistory(true)}
                  title="Versions — save and view past proposal snapshots"
                  aria-label="Versions"
                >
                  <History size={14} />
                </button>
              )}
              {/* Tools Dropdown */}
              <div className="tools-dropdown-wrapper">
                <button className="tools-header-btn" onClick={() => setShowToolsMenu(!showToolsMenu)} title="Tools">
                  <Download size={18} />
                  <span>Tools</span>
                  <ChevronDown size={14} />
                </button>
                {showToolsMenu && (
                  <div className="tools-dropdown-menu">
                    <button
                      onClick={() => {
                        if (!isSent) {
                          openTimberInput();
                          setShowToolsMenu(false);
                        }
                      }}
                      disabled={isSent}
                      style={isSent ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                      title="Compare a project timber CSV side-by-side and pick individual lines"
                    >
                      <TreePine size={16} /> Timber Input
                    </button>
                    {isStaffBuilderUser && (
                      <button onClick={handleInternalReview} disabled={internalReviewLoading}>
                        <ClipboardList size={16} />{' '}
                        {internalReviewLoading ? 'Generating Internal Review…' : 'Internal Review'}
                      </button>
                    )}
                    {isStaffBuilderUser && (
                      <button onClick={handleDebugPricing} disabled={debugPricingLoading}>
                        <Bug size={16} />{' '}
                        {debugPricingLoading ? 'Generating Debug Pricing…' : 'Debug Pricing'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setShowTimberSupplierSummary(true);
                        setShowToolsMenu(false);
                      }}
                    >
                      <TreePine size={16} /> Timber summary
                    </button>
                    <button onClick={handleExportTimberCSV}>
                      <Download size={16} /> Export Timber CSV
                    </button>
                    <button onClick={handleExportTimberPDF}>
                      <Download size={16} /> Export Timber PDF
                    </button>
                    <button onClick={handleExportDeckingCSV}>
                      <Download size={16} /> Export Decking CSV
                    </button>
                    <button onClick={handleExportDeckingPDF}>
                      <Download size={16} /> Export Decking PDF
                    </button>
                    {isStaffBuilderUser && (
                      <button
                        type="button"
                        disabled={stagingRfqBusy || !proposalState || isSent}
                        onClick={() => {
                          setShowToolsMenu(false);
                          void handleStageForSupplierRfq();
                        }}
                      >
                        {stagingRfqBusy ? <Loader2 size={16} className="pb-spin" /> : <Send size={16} />}
                        Stage for supplier RFQ
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {showHardwareListBanner && hardwarePrompt && (
            <div className="timber-list-banner" role="status">
              <div className="timber-list-banner-text">
                <Wrench size={18} aria-hidden />
                <div>
                  <strong>
                    {appliedHardwareFileMatches ? 'Hardware list applied' : 'Hardware list on file'}
                  </strong>
                  <span className="timber-list-banner-sub">
                    {hardwarePrompt.fileName}
                    {hardwarePrompt.uploadedByName ? ` · uploaded by ${hardwarePrompt.uploadedByName}` : ''}
                    {hardwarePromptUploadedLabel ? ` · ${hardwarePromptUploadedLabel}` : ''}
                  </span>
                  <p className="timber-list-banner-question">
                    {appliedHardwareFileMatches
                      ? 'This steel CSV is already on the mapped structures. Upload a newer hardware list to update it.'
                      : 'Map each storey in this CSV to a structure. Shared storey tags copy onto every mapped structure.'}
                  </p>
                </div>
              </div>
              <div className="timber-list-banner-actions">
                {hardwareBannerCanEdit ? (
                  showImportHardwareFromProjectFile && (
                    <button
                      type="button"
                      className="timber-list-banner-primary"
                      onClick={() => void handleImportFromProjectHardwareList()}
                    >
                      <Upload size={14} /> Import hardware list
                    </button>
                  )
                ) : (
                  <span className="timber-list-banner-muted">
                    {isSent ? 'Editing is limited while the proposal is sent. Open Project Files to access the hardware list CSV.' : ''}
                  </span>
                )}
                <button type="button" className="timber-list-banner-dismiss" onClick={() => void handleDismissHardwareListBanner()}>
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {hardwareParseResult && proposalState && (
            <HardwareListApplyModal
              fileName={hardwarePrompt?.fileName || 'hardware-list.csv'}
              storeys={hardwareParseResult.storeys}
              items={hardwareParseResult.items}
              nodeSets={proposalState.nodeSets}
              onCancel={() => setHardwareParseResult(null)}
              onApply={(map) => void handleApplyHardwareList(map)}
            />
          )}

          {/* Phased project manager (beta) */}
          {proposalState && phasedFeatureVisible && (
            <ProposalPhasePanel
              phasingEnabled={proposalState.phasingEnabled === true}
              phases={proposalState.phases || []}
              nodeSets={proposalState.nodeSets}
              profitMargin={proposalState.profitMargin}
              proposalApproved={isSignedCommercial}
              onTogglePhasing={handleTogglePhasing}
              onAddPhase={handleAddPhase}
              onRenamePhase={handleRenamePhase}
              onMovePhase={handleMovePhase}
              onDeletePhase={handleDeletePhase}
              onSetPhaseStatus={handleSetPhaseStatus}
            />
          )}

          {/* 3-Wide Grid Layout */}
          <div className="builder-grid-layout">
            {proposalState && rates && (
              <>
                {proposalState.nodeSets.map(nodeSet => {
                  const canMarkOptional = canMarkClientOptional(proposalState.nodeSets, nodeSet.id);
                  const phasingOn = proposalState.phasingEnabled === true && (proposalState.phases?.length || 0) > 0;
                  const nodeSetPhaseId = phasingOn
                    ? getNodeSetPhaseId({ phases: proposalState.phases || [] }, nodeSet)
                    : null;
                  const nodeSetPhase = phasingOn
                    ? getProposalPhaseById({ phases: proposalState.phases || [] }, nodeSetPhaseId)
                    : null;

                  return (
                  <StructureGridCard
                    key={nodeSet.id}
                    nodeSet={nodeSet}
                    onClick={() => setExpandedCardId(nodeSet.id)}
                    kitMode={proposalState.isKitProposal && !proposalState.isCustomized}
                    availableSpecies={proposalState.kitAvailableSpecies || proposalState.offeredSpecies || []}
                    selectedSpecies={
                      isSignedCommercial && signedSpeciesSelections
                        ? proposalState.isKitProposal
                          ? signedSpeciesSelections.kitSelectedSpecies
                            ? [signedSpeciesSelections.kitSelectedSpecies]
                            : []
                          : signedSpeciesSelections.selectedSpecies[nodeSet.id]
                            ? [signedSpeciesSelections.selectedSpecies[nodeSet.id]]
                            : []
                        : proposalState.activeSpecies
                          ? [proposalState.activeSpecies]
                          : []
                    }
                    onSpeciesToggle={
                      !isSignedCommercial
                        ? (species) => {
                            setProposalState(prev => (prev ? { ...prev, activeSpecies: species } : prev));
                            setHasChanges(true);
                          }
                        : undefined
                    }
                    onToggleEnabled={!isSent ? handleToggleEnabled : undefined}
                    onToggleClientOptional={!isApproved ? handleToggleClientOptional : undefined}
                    canMarkOptional={canMarkOptional}
                    onToggleClientExclusive={!isApproved ? handleToggleClientExclusive : undefined}
                    phaseOptions={
                      phasingOn
                        ? getOrderedPhases({ phases: proposalState.phases || [] }).map(p => ({
                            id: p.id,
                            name: p.name,
                            committed: p.status === 'committed',
                          }))
                        : undefined
                    }
                    phaseId={nodeSetPhaseId}
                    onPhaseChange={handleAssignStructurePhase}
                    phaseLocked={nodeSetPhase?.status === 'committed'}
                    disabled={isSent}
                    timberListHasIssues={structureHasTimberListIssues(nodeSet)}
                    timberOrderLengthUnder8Min={structureHasShortOrderLengthTimber(nodeSet)}
                    projectSharePercent={structureSharePercents[nodeSet.id] ?? null}
                    usesGlobalStructureCosting={true}
                  />
                  );
                })}
                {!isSent && (
                  <AddStructureCard onClick={handleAddNodeSet} />
                )}
              </>
            )}
          </div>

          {/* Structure compare — two detail panes, highlights live from proposalState */}
          {compareSession && proposalState && rates && (() => {
            const right = compareSession.right;
            const leftNodeSet = proposalState.nodeSets.find(ns => ns.id === compareSession.leftNodeSetId);
            const rightNodeSet = right.source === 'local'
              ? proposalState.nodeSets.find(ns => ns.id === right.nodeSetId)
              : right.nodeSet;
            if (!leftNodeSet || !rightNodeSet) return null;
            const remoteLabel = right.source === 'remote' ? right.dealTitle : null;
            const remoteDealId = right.source === 'remote' ? right.dealId : null;
            const rightIsRemote = right.source === 'remote';
            const closeCompare = () => {
              setCompareSession(null);
              setShowComparePicker(false);
              if (hasChangesRef.current && dealId && !isSent && !isSignedCommercial) {
                void saveProposalInternal();
              }
            };
            const paneProps = {
              dealId,
              exportDealTitle: deal?.title,
              allNodeSets: proposalState.nodeSets,
              rates,
              profitMargin: proposalState.profitMargin,
              allowPostSendDesignEdits: false,
              kitMode: proposalState.isKitProposal && !proposalState.isCustomized,
              globalCosts: proposalState.globalCosts,
              hideStructureFreightAndOverhead: true,
              opsReviewPending: opsReviewMeta?.status === 'pending',
              opsReviewActive: isStaffBuilderUser && !isSent,
              opsReviewComments,
              canOpsReviewComment,
              recommendedTimberDefaults: lastTimberDefaults,
              onRememberTimberDefaults: setLastTimberDefaults,
              projectId: projectIdForFiles,
              layout: 'pane' as const,
              designEstimating: proposalState.designEstimating,
              proposalStatus: proposalData?.status ?? null,
              structureMentionProjectOptions: opsStructureMentionProjectOptions,
            };
            return (
              <div className="structure-compare-shell">
                <div className="structure-compare-shell-bar">
                  <span className="structure-compare-shell-title">
                    Comparing {leftNodeSet.name}
                    {' · '}
                    {remoteLabel
                      ? `${rightNodeSet.name} (${remoteLabel})`
                      : rightNodeSet.name}
                  </span>
                  <div className="structure-compare-shell-actions">
                    <button
                      type="button"
                      className={`structure-compare-sync-btn${compareScrollSync ? ' is-on' : ''}`}
                      onClick={() => setCompareScrollSync(on => !on)}
                      title={compareScrollSync ? 'Turn off scroll sync (S)' : 'Turn on scroll sync (S)'}
                    >
                      {compareScrollSync ? 'Sync on' : 'Sync off'}
                    </button>
                    <button type="button" className="structure-compare-shell-close" onClick={closeCompare}>
                      <X size={14} /> Close compare
                    </button>
                  </div>
                </div>
                <div className="structure-compare-panes" ref={comparePanesRef}>
                  <StructureDetailModal
                    {...paneProps}
                    key={`compare-left-${leftNodeSet.id}`}
                    nodeSet={leftNodeSet}
                    compareOtherNodeSet={rightNodeSet}
                    disabled={isSent}
                    onClose={closeCompare}
                    onUpdate={isSent ? () => undefined : (updates) => handleUpdateNodeSet(leftNodeSet.id, updates)}
                    onDelete={isSent ? () => undefined : () => handleDeleteRef.current(leftNodeSet.id)}
                    onRename={isSent ? () => undefined : (newName) => handleRenameRef.current(leftNodeSet.id, newName)}
                    onAddOpsReviewComment={
                      isStaffBuilderUser
                        ? (message, nodeType, nodeLabel) =>
                            handleAddOpsReviewComment(leftNodeSet.id, leftNodeSet.name, message, nodeType, nodeLabel)
                        : undefined
                    }
                    projectSharePercent={structureSharePercents[leftNodeSet.id] ?? null}
                    onAddTimber={isSent ? undefined : () => openTimberInput(leftNodeSet.id)}
                  />
                  <StructureDetailModal
                    {...paneProps}
                    key={`compare-right-${rightNodeSet.id}-${remoteDealId ?? 'local'}`}
                    dealId={remoteDealId ?? dealId}
                    nodeSet={rightNodeSet}
                    compareOtherNodeSet={leftNodeSet}
                    comparePaneLabel={remoteLabel ? `From ${remoteLabel}` : undefined}
                    allNodeSets={rightIsRemote ? [rightNodeSet] : proposalState.nodeSets}
                    disabled={isSent || rightIsRemote}
                    onClose={closeCompare}
                    onUpdate={rightIsRemote ? () => undefined : (updates) => handleUpdateNodeSet(rightNodeSet.id, updates)}
                    onDelete={rightIsRemote ? () => undefined : () => handleDeleteRef.current(rightNodeSet.id)}
                    onRename={rightIsRemote ? () => undefined : (newName) => handleRenameRef.current(rightNodeSet.id, newName)}
                    onAddOpsReviewComment={
                      !rightIsRemote && isStaffBuilderUser
                        ? (message, nodeType, nodeLabel) =>
                            handleAddOpsReviewComment(rightNodeSet.id, rightNodeSet.name, message, nodeType, nodeLabel)
                        : undefined
                    }
                    projectSharePercent={rightIsRemote ? null : (structureSharePercents[rightNodeSet.id] ?? null)}
                    onAddTimber={isSent || rightIsRemote ? undefined : () => openTimberInput(rightNodeSet.id)}
                  />
                </div>
              </div>
            );
          })()}

          {/* Expanded Card Modal */}
          {expandedCardId && !compareSession && proposalState && rates && (
            <StructureDetailModal
              dealId={dealId}
              exportDealTitle={deal?.title}
              key={expandedCardId}
              nodeSet={proposalState.nodeSets.find(ns => ns.id === expandedCardId)!}
              allNodeSets={proposalState.nodeSets}
              rates={rates}
              profitMargin={proposalState.profitMargin}
              onClose={() => {
                setExpandedCardId(null);
                if (hasChangesRef.current && dealId && !isSignedCommercial) {
                  void saveProposalInternal();
                }
              }}
              onUpdate={(updates) => handleUpdateNodeSet(expandedCardId, updates)}
              allowPostSendDesignEdits={isSent && !isApproved}
              onDelete={() => handleDeleteRef.current(expandedCardId)}
              onRename={(newName) => handleRenameRef.current(expandedCardId, newName)}
              kitMode={proposalState.isKitProposal && !proposalState.isCustomized}
              disabled={isSent}
              globalCosts={proposalState.globalCosts}
              hideStructureFreightAndOverhead={true}
              opsReviewPending={opsReviewMeta?.status === 'pending'}
              opsReviewActive={isStaffBuilderUser && !isSent}
              opsReviewComments={opsReviewComments}
              canOpsReviewComment={canOpsReviewComment}
              onAddOpsReviewComment={
                isStaffBuilderUser
                  ? (message, nodeType, nodeLabel) =>
                      handleAddOpsReviewComment(
                        expandedCardId,
                        proposalState.nodeSets.find(ns => ns.id === expandedCardId)?.name || 'Structure',
                        message,
                        nodeType,
                        nodeLabel
                      )
                  : undefined
              }
              projectSharePercent={structureSharePercents[expandedCardId] ?? null}
              recommendedTimberDefaults={lastTimberDefaults}
              onRememberTimberDefaults={setLastTimberDefaults}
              projectId={projectIdForFiles}
              shouldAutoApplyTimberDefaults={
                expandedCardId ? !autoAppliedTimberDefaultsRef.current.has(expandedCardId) : false
              }
              onTimberDefaultsAutoApplied={(structureId) => {
                autoAppliedTimberDefaultsRef.current.add(structureId);
              }}
              onAddTimber={isSent ? undefined : () => openTimberInput(expandedCardId)}
              designEstimating={proposalState.designEstimating}
              proposalStatus={proposalData?.status ?? null}
              structureMentionProjectOptions={opsStructureMentionProjectOptions}
            />
          )}

          {/* Change-order contract banner (signed proposals with approved COs) */}
          {isSignedCommercial && contractScheduleBreakdown && approvedChangeOrderTotal !== 0 && (
            <div
              style={{
                margin: '12px 0',
                padding: '12px 16px',
                borderRadius: 8,
                border: '1px solid var(--color-border)',
                background: 'var(--color-surface)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  Signed contract <strong style={{ color: 'var(--color-text-primary)' }}>{formatCurrency(contractScheduleBreakdown.baseTotal)}</strong>
                  {' '}+ approved change orders{' '}
                  <strong style={{ color: approvedChangeOrderTotal < 0 ? '#b4453a' : '#2c6b3c' }}>
                    {approvedChangeOrderTotal >= 0 ? '+' : ''}{formatCurrency(approvedChangeOrderTotal)}
                  </strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--pm-accent)' }}>
                    Current contract value: {formatCurrency(contractScheduleBreakdown.currentContractValue)}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowContractSchedule(v => !v)}
                    style={{
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-surface)',
                      borderRadius: 6,
                      padding: '4px 10px',
                      fontSize: 12,
                      cursor: 'pointer',
                      color: 'var(--pm-accent)',
                    }}
                  >
                    {showContractSchedule ? 'Hide payment schedule' : 'Payment schedule'}
                  </button>
                </div>
              </div>
              {showContractSchedule && (
                <div style={{ marginTop: 12 }}>
                  <PaymentScheduleBreakdown breakdown={contractScheduleBreakdown} variant="light" compact />
                </div>
              )}
            </div>
          )}

          {/* Summary Bar */}
          {proposalState && rates && (
            <ProposalSummary
              nodeSets={proposalState.nodeSets.filter(ns => ns.enabled !== false)}
              profitMargin={proposalState.profitMargin}
              signedContractTotals={signedContractFooterTotals}
              onProfitMarginChange={handleUpdateProfitMargin}
              onPreview={() => setShowPreview(true)}
              installOptional={proposalState.installOptional}
              onInstallOptionalChange={(value) => {
                isLocalEditRef.current = true;
                const updatedState = {
                  ...proposalState,
                  installOptional: value,
                };
                setProposalState(updatedState);
                saveProposalInternal(updatedState);
              }}
              designIncludedInKit={proposalState.designIncludedInKit}
              onDesignIncludedChange={(value) => {
                isLocalEditRef.current = true;
                const updatedState = {
                  ...proposalState,
                  designIncludedInKit: value,
                };
                setProposalState(updatedState);
                saveProposalInternal(updatedState);
              }}
              isRomPricing={proposalState.isRomPricing}
              onRomChange={(value) => {
                isLocalEditRef.current = true;
                const updatedState = {
                  ...proposalState,
                  isRomPricing: value,
                };
                setProposalState(updatedState);
                saveProposalInternal(updatedState);
              }}
              projectSquareFootage={proposalState.projectSquareFootage}
              onProjectSquareFootageChange={(value) => {
                isLocalEditRef.current = true;
                const updatedState = {
                  ...proposalState,
                  projectSquareFootage: value,
                };
                setProposalState(updatedState);
                saveProposalInternal(updatedState);
              }}
              showCostPerSqFt={!!proposalData?.publicSettings?.showCostPerSqFt}
              kitMode={proposalState.isKitProposal && !proposalState.isCustomized}
              activeSpecies={
                isSignedCommercial && signedSpeciesDisplayLabel
                  ? signedSpeciesDisplayLabel
                  : proposalState.activeSpecies
              }
              signedSpeciesLabel={signedSpeciesDisplayLabel}
              kitPricing={proposalState.kitPricing}
              isSent={isSent}
              globalCosts={proposalState.globalCosts}
              overheadPercentOfProject={getEffectiveProjectOverheadPercent(proposalState.globalCosts)}
              discount={proposalState.discount}
              designEstimating={proposalState.designEstimating}
              proposalStatus={proposalData?.status}
              designRates={rates}
            />
          )}
        </div>
      )
      }

      {updateModal && createPortal(
        <TimberListUpdateModal
          key={updateModal.fileMeta?.fileId ?? 'local'}
          diff={updateModal.diff}
          source={updateModal.fileMeta}
          sourceOptions={projectTimberCsvs.length > 1 ? projectTimberCsvs : undefined}
          selectedSourceId={updateModal.fileMeta?.fileId}
          appliedSourceId={proposalData?.timberListAppliedToProposalFileId}
          onSelectSource={(fileId) => {
            const file = projectTimberCsvs.find(f => f.id === fileId);
            if (file) void handleSwitchTimberUpdateSource(file);
          }}
          sourceSwitchLoading={!!pickingProjectCsvId}
          // Validation warnings collected from the parsed CSV so the user is
          // forewarned about issues that the existing parser already flags.
          validationNotices={updateModal.parsed.errors.length > 0
            ? [`CSV parser reported ${updateModal.parsed.errors.length} row issue${updateModal.parsed.errors.length === 1 ? '' : 's'} (skipped or partially parsed).`]
            : undefined}
          readOnly={isSent}
          onCancel={() => setUpdateModal(null)}
          onApply={(selections, opts) => void handleApplyTimberDiff(selections, opts)}
        />,
        document.body,
      )}

      {comparePickModal && proposalState && createPortal(
        <TimberComparePickModal
          key={comparePickModal.fileMeta?.fileId ?? 'local'}
          nodeSets={
            comparePickModal.lockToNodeSetId
              ? proposalState.nodeSets.filter(ns => ns.id === comparePickModal.lockToNodeSetId)
              : proposalState.nodeSets
          }
          parsed={comparePickModal.parsed}
          source={comparePickModal.fileMeta}
          sourceOptions={projectTimberCsvs.length > 1 ? projectTimberCsvs : undefined}
          selectedSourceId={comparePickModal.fileMeta?.fileId}
          appliedSourceId={proposalData?.timberListAppliedToProposalFileId}
          onSelectSource={(fileId) => {
            const file = projectTimberCsvs.find(f => f.id === fileId);
            if (file) void handleSwitchComparePickSource(file);
          }}
          sourceSwitchLoading={!!pickingProjectCsvId}
          readOnly={isSent}
          lockToNodeSetId={comparePickModal.lockToNodeSetId}
          onCancel={() => {
            setComparePickModal(null);
            setTimberInputLockToNodeSetId(null);
          }}
          onCreate={(payload) => void handleCreateStructureFromComparePick(payload)}
        />,
        document.body,
      )}

      {/* Lightweight ephemeral toast for diff-flow confirmations. Portaled so
          it sits above any other modal/overlay z-index without needing a CSS
          stacking-context dance. */}
      {flashMsg && createPortal(
        <div className="proposal-flash-toast" role="status" aria-live="polite">
          {flashMsg}
        </div>,
        document.body,
      )}

      {/* Quick 3D model preview popup. Portaled to body so the iframe escapes
          any local stacking contexts and overlays the entire proposal page. */}
      {showHtmlModelPopup && latestHtmlModel && createPortal(
        <div
          className="pb-3d-popup-overlay"
          onClick={() => setShowHtmlModelPopup(false)}
          role="presentation"
        >
          <div
            className="pb-3d-popup"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="3D model preview"
          >
            <div className="pb-3d-popup-header">
              <div className="pb-3d-popup-title">
                <Box size={16} aria-hidden />
                <span className="pb-3d-popup-title-name">{latestHtmlModel.name}</span>
                {latestHtmlModel.versionLabel && (
                  <span className="pb-3d-popup-version">{latestHtmlModel.versionLabel}</span>
                )}
              </div>
              <div className="pb-3d-popup-actions">
                <a
                  href={portalHostedAssetHref(latestHtmlModel.url, { downloadName: latestHtmlModel.name })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pb-3d-popup-icon-btn"
                  title="Open in new tab"
                >
                  <ExternalLink size={14} />
                </a>
                <button
                  type="button"
                  className="pb-3d-popup-icon-btn"
                  onClick={() => setShowHtmlModelPopup(false)}
                  title="Close"
                  aria-label="Close 3D model preview"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
            <iframe
              key={latestHtmlModel.id}
              src={portalHostedAssetHref(latestHtmlModel.url, { downloadName: latestHtmlModel.name })}
              title={`3D model: ${latestHtmlModel.name}`}
              className="pb-3d-popup-iframe"
              sandbox="allow-scripts allow-same-origin allow-popups allow-pointer-lock"
              loading="lazy"
            />
          </div>
        </div>,
        document.body,
      )}

      {internalReviewPdf && createPortal(
        <div
          className="pb-3d-popup-overlay"
          onClick={handleCloseInternalReview}
          role="presentation"
        >
          <div
            className="pb-3d-popup"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Internal Review preview"
          >
            <div className="pb-3d-popup-header">
              <div className="pb-3d-popup-title">
                <ClipboardList size={16} aria-hidden />
                <span className="pb-3d-popup-title-name">Internal Review</span>
                <span className="pb-3d-popup-version">PREVIEW</span>
              </div>
              <div className="pb-3d-popup-actions">
                <button
                  type="button"
                  className="pb-3d-popup-icon-btn pb-3d-popup-icon-btn--primary"
                  onClick={handleDownloadInternalReview}
                  title={`Download ${internalReviewPdf.filename}`}
                  aria-label="Download Internal Review PDF"
                >
                  <Download size={14} />
                </button>
                <a
                  href={internalReviewPdf.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pb-3d-popup-icon-btn"
                  title="Open in new tab"
                >
                  <ExternalLink size={14} />
                </a>
                <button
                  type="button"
                  className="pb-3d-popup-icon-btn"
                  onClick={handleCloseInternalReview}
                  title="Close"
                  aria-label="Close Internal Review preview"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
            <iframe
              key={internalReviewPdf.url}
              // #toolbar=1&navpanes=0 hints to Chromium/Edge's built-in viewer
              // to show the toolbar (zoom/print) but hide the side nav.
              src={`${internalReviewPdf.url}#toolbar=1&navpanes=0`}
              title={`Internal Review: ${internalReviewPdf.filename}`}
              className="pb-3d-popup-iframe"
            />
          </div>
        </div>,
        document.body,
      )}

      {timberImportIssueModal &&
        createPortal(
          <div className="trn-backdrop" onClick={() => setTimberImportIssueModal(null)} role="presentation">
            <div
              className={`trn-dialog ${timberImportIssueModal.blockedAutoRfqOnlyForOrderLength ? 'trn-dialog--amber' : 'trn-dialog--rose'}`}
              onClick={e => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="timber-import-issue-title"
            >
              <header className="trn-header">
                <div
                  className={`trn-icon-wrap ${timberImportIssueModal.blockedAutoRfqOnlyForOrderLength ? 'trn-icon-wrap--amber' : 'trn-icon-wrap--rose'}`}
                  aria-hidden
                >
                  <AlertCircle size={22} strokeWidth={2.25} />
                </div>
                <h2 id="timber-import-issue-title" className="trn-title">
                  {timberImportIssueModal.blockedAutoRfqOnlyForOrderLength
                    ? 'Supplier RFQ not opened automatically'
                    : 'Timber list needs attention'}
                </h2>
                <button type="button" className="trn-close" onClick={() => setTimberImportIssueModal(null)} aria-label="Close">
                  <X size={20} />
                </button>
              </header>
              <div className="trn-body">
                {timberImportIssueModal.blockedAutoRfqOnlyForOrderLength ? (
                  <>
                    <div className="trn-callout trn-callout--amber">
                      <p className="trn-lead">
                        The automatic supplier print was skipped: at least one timber line is under <strong>8&apos; order length</strong>.
                      </p>
                      <ul className="trn-steps">
                        <li>
                          On structure cards, look for the <strong>8&apos; Order Length Min</strong> badge.
                        </li>
                        <li>
                          When ready, open <strong>Tools</strong> → <strong>Export Timber PDF</strong>.
                        </li>
                      </ul>
                    </div>
                    {timberImportIssueModal.structureNamesWithIssues.length > 0 && (
                      <div className="trn-section">
                        <p className="trn-section-label">Structures with short order lengths</p>
                        <div className="trn-chips" role="list">
                          {timberImportIssueModal.structureNamesWithIssues.map(name => (
                            <span key={name} className="trn-chip" role="listitem">
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="trn-callout trn-callout--rose">
                      <p className="trn-lead">
                        This proposal is not ready for a supplier request until timber lines are valid. Open structures with the <strong>Pricing Needed</strong> badge and fix piece dimensions, species, quantity, and board feet. If rows were skipped from the CSV, fix the source file or add pieces manually.
                      </p>
                    </div>
                    {timberImportIssueModal.csvErrors.length > 0 && (
                      <div className="trn-csv-panel">
                        <strong className="trn-csv-title">
                          CSV rows skipped or incomplete ({timberImportIssueModal.csvErrors.length})
                        </strong>
                        <ul className="trn-csv-list">
                          {timberImportIssueModal.csvErrors.slice(0, 15).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                        {timberImportIssueModal.csvErrors.length > 15 && (
                          <p className="trn-csv-more">
                            …and {timberImportIssueModal.csvErrors.length - 15} more
                          </p>
                        )}
                      </div>
                    )}
                    {timberImportIssueModal.structureNamesWithIssues.length > 0 && (
                      <div className="trn-section">
                        <p className="trn-section-label">Structures with line-item issues</p>
                        <div className="trn-chips" role="list">
                          {timberImportIssueModal.structureNamesWithIssues.map(name => (
                            <span key={name} className="trn-chip" role="listitem">
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="trn-footer">
                <button type="button" className="trn-btn trn-btn--primary" onClick={() => setTimberImportIssueModal(null)}>
                  OK
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {timberExportWarningModal &&
        proposalState &&
        createPortal(
          <div className="trn-backdrop" onClick={() => setTimberExportWarningModal(null)} role="presentation">
            <div
              className="trn-dialog trn-dialog--amber"
              onClick={e => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="timber-export-warn-title"
            >
              <header className="trn-header">
                <div className="trn-icon-wrap trn-icon-wrap--amber" aria-hidden>
                  <AlertCircle size={22} strokeWidth={2.25} />
                </div>
                <h2 id="timber-export-warn-title" className="trn-title">
                  Fix timber lists first
                </h2>
                <button type="button" className="trn-close" onClick={() => setTimberExportWarningModal(null)} aria-label="Close">
                  <X size={20} />
                </button>
              </header>
              <div className="trn-body">
                <p className="trn-lead">
                  One or more structures still have timber list errors. Correct them in structure details before sending a supplier RFQ, or export anyway if you intend to fix the PDF manually.
                </p>
                <div className="trn-section">
                  <p className="trn-section-label">Structures with issues</p>
                  <div className="trn-chips" role="list">
                    {timberExportWarningModal.structureNames.map(n => (
                      <span key={n} className="trn-chip" role="listitem">
                        {n}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="trn-footer">
                <button type="button" className="trn-btn trn-btn--secondary" onClick={() => setTimberExportWarningModal(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="trn-btn trn-btn--primary"
                  onClick={() => {
                    setTimberExportWarningModal(null);
                    runTimberSupplierPdfExport(proposalState);
                  }}
                >
                  Export anyway
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* CSV Import Modal */}
      {
        showCSVImport && (
          <div className="preview-modal-overlay csv-import-overlay" onClick={closeTimberInputPicker}>
            <div className="preview-modal csv-import-modal" onClick={(e) => e.stopPropagation()}>
              <div className="preview-modal-header">
                <h2>
                  <TreePine size={18} />
                  Timber Input
                </h2>
                <button className="close-preview-btn" onClick={closeTimberInputPicker}>
                  <X size={18} />
                </button>
              </div>
              <div className="preview-modal-content">
                {!csvParseResult ? (
                  <>
                    <p className="csv-picker-intro">
                      {timberInputLockToNodeSetId
                        ? `Pick a timber CSV and add selected lines to ${
                            proposalState?.nodeSets.find(ns => ns.id === timberInputLockToNodeSetId)?.name
                            || 'this structure'
                          }.`
                        : 'Pick a timber CSV to open the side-by-side compare view. Select individual lines from the proposal and/or CSV to build a new custom structure.'}
                    </p>

                    <div className="csv-picker-section-label">
                      <span>From this project</span>
                      {projectTimberCsvs.length > 0 && (
                        <small>{projectTimberCsvs.length} version{projectTimberCsvs.length === 1 ? '' : 's'} available</small>
                      )}
                    </div>

                    {projectTimberCsvsLoading ? (
                      <div className="csv-source-skeleton">
                        <div className="csv-source-skeleton-row" />
                        <div className="csv-source-skeleton-row" />
                      </div>
                    ) : projectTimberCsvs.length === 0 ? (
                      <div className="csv-source-empty">
                        <FileText size={20} aria-hidden />
                        <strong>No timber CSVs found</strong>
                        <span>
                          {projectIdForFiles
                            ? <>Upload a CSV in <em>Project Hub → Files</em> with the <strong> Timber list</strong> tag to see it here.</>
                            : <>This deal doesn’t have a project yet, so there are no linked Hub files.</>}
                        </span>
                      </div>
                    ) : (
                      <div className="csv-source-list">
                        {projectTimberCsvs.map((file, idx) => {
                          const isLatest = idx === 0;
                          const isApplied = !!proposalData?.timberListAppliedToProposalFileId
                            && proposalData.timberListAppliedToProposalFileId === file.id;
                          const isPicking = pickingProjectCsvId === file.id;
                          const dateLabel = file.uploadedAt instanceof Date
                            ? file.uploadedAt.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                            : '';
                          return (
                            <button
                              key={file.id}
                              type="button"
                              className={`csv-source-row${isLatest ? ' csv-source-row--latest' : ''}`}
                              onClick={() => void handlePickProjectCsv(file)}
                              disabled={!!pickingProjectCsvId}
                              title={isApplied ? 'This CSV is already applied to the proposal' : `Use ${file.name}`}
                            >
                              <span className="csv-source-icon" aria-hidden>
                                <FileText size={18} />
                              </span>
                              <span className="csv-source-body">
                                <span className="csv-source-title">
                                  <span className="csv-source-title-name">{file.name}</span>
                                  {isLatest && <span className="csv-source-badge">Latest</span>}
                                  {isApplied && <span className="csv-source-badge csv-source-badge--applied">Applied</span>}
                                  {!isLatest && file.versionLabel && (
                                    <span className="csv-source-badge csv-source-badge--version">{file.versionLabel}</span>
                                  )}
                                </span>
                                <span className="csv-source-meta">
                                  {dateLabel && <span>{dateLabel}</span>}
                                  {file.uploadedByName && (
                                    <>
                                      {dateLabel && <span className="csv-source-meta-dot">·</span>}
                                      <span>by {file.uploadedByName}</span>
                                    </>
                                  )}
                                </span>
                              </span>
                              <span className="csv-source-action">
                                {isPicking ? (
                                  <>
                                    <span className="csv-source-spinner" aria-hidden />
                                    Loading…
                                  </>
                                ) : (
                                  'Open'
                                )}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                  </>
                ) : (
                  <div>
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                      <div style={{ flex: 1, background: 'var(--color-surface)', padding: '12px', borderRadius: '8px' }}>
                        <strong>CSV Type:</strong> {csvParseResult.csvType === 'production' ? 'Production' : 'Materials'}
                      </div>
                      <div style={{ flex: 1, background: 'var(--color-surface)', padding: '12px', borderRadius: '8px' }}>
                        <strong>Items:</strong> {csvParseResult.totalItems}
                      </div>
                      <div style={{ flex: 1, background: 'var(--color-surface)', padding: '12px', borderRadius: '8px' }}>
                        <strong>Groups:</strong> {csvParseResult.groups.length}
                      </div>
                    </div>

                    {csvParseResult.errors.length > 0 && (
                      <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-error-text)', marginBottom: '8px' }}>
                          <AlertCircle size={16} />
                          <strong>Warnings ({csvParseResult.errors.length})</strong>
                        </div>
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: 'var(--color-error-text)' }}>
                          {csvParseResult.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                          {csvParseResult.errors.length > 5 && (
                            <li>...and {csvParseResult.errors.length - 5} more</li>
                          )}
                        </ul>
                      </div>
                    )}

                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ marginBottom: '8px' }}>Structures to Create:</h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {csvParseResult.groups.map(group => {
                          const count = csvParseResult.items.filter(i => i.group === group).length;
                          return (
                            <span key={group} style={{ background: 'var(--color-info-light)', color: 'var(--color-info-text)', padding: '6px 12px', borderRadius: '6px', fontSize: '14px' }}>
                              {group} ({count} items)
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ background: 'var(--color-surface)', borderRadius: '8px', padding: '12px', maxHeight: '200px', overflowY: 'auto' }}>
                      <h4 style={{ marginBottom: '8px' }}>Preview (first 10 items):</h4>
                      <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                            <th style={{ textAlign: 'left', padding: '4px' }}>Piece</th>
                            <th style={{ textAlign: 'left', padding: '4px' }}>Group</th>
                            <th style={{ textAlign: 'left', padding: '4px' }}>Material</th>
                            <th style={{ textAlign: 'left', padding: '4px' }}>Species</th>
                            <th style={{ textAlign: 'left', padding: '4px' }}>Grade</th>
                            <th style={{ textAlign: 'center', padding: '4px' }}>Qty</th>
                            <th style={{ textAlign: 'left', padding: '4px' }}>Dimensions</th>
                            <th style={{ textAlign: 'right', padding: '4px' }}>BdFt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvParseResult.items.slice(0, 10).map((item, i) => {
                            const { species, grade } = parseTimberMaterialColumn(item.material);
                            return (
                            <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                              <td style={{ padding: '4px' }}>{item.pieceName}</td>
                              <td style={{ padding: '4px' }}>{item.group}</td>
                              <td style={{ padding: '4px' }}>{item.material || '—'}</td>
                              <td style={{ padding: '4px' }}>{species}</td>
                              <td style={{ padding: '4px' }}>{grade}</td>
                              <td style={{ textAlign: 'center', padding: '4px' }}>{item.quantity}</td>
                              <td style={{ padding: '4px' }}>{item.normalizedDimensions}</td>
                              <td style={{ textAlign: 'right', padding: '4px' }}>{(item.boardFeet * item.quantity).toFixed(1)}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {csvParseResult.items.length > 10 && (
                        <p style={{ marginTop: '8px', color: 'var(--color-text-secondary)', fontSize: '12px' }}>
                          ...and {csvParseResult.items.length - 10} more items
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {csvParseResult && (
                <div className="preview-modal-footer" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    className="action-button secondary"
                    onClick={handleCSVImportReset}
                    disabled={importing}
                  >
                    Choose Different File
                  </button>
                  <button
                    className="action-button primary"
                    onClick={handleCSVImportConfirm}
                    disabled={importing || csvParseResult.items.length === 0}
                  >
                    {importing ? 'Importing...' : `Import ${csvParseResult.groups.length} Structure${csvParseResult.groups.length !== 1 ? 's' : ''}`}
                  </button>
                </div>
              )}
            </div>
          </div>
        )
      }

      {/* Coming Soon Popup for Kit Proposals */}
      {
        showComingSoonPopup && (
          <div className="coming-soon-overlay" onClick={() => setShowComingSoonPopup(false)}>
            <div className="coming-soon-modal" onClick={(e) => e.stopPropagation()}>
              <div className="coming-soon-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
              </div>
              <h3>Feature Coming Soon</h3>
              <p>Adding multiple structures to kit proposals is currently in development.</p>
              <p className="coming-soon-subtitle">For now, kit proposals include one structure from the selected kit.</p>
              <button className="coming-soon-button" onClick={() => setShowComingSoonPopup(false)}>
                Got it
              </button>
            </div>
          </div>
        )
      }
      {/* Global Costs Modal */}
      {
        showGlobalCosts && proposalState && (
          <GlobalCostsModal
            globalCosts={proposalState.globalCosts}
            nodeSets={proposalState.nodeSets}
            designEstimating={proposalState.designEstimating}
            rates={rates}
            proposalStatus={proposalData?.status}
            clientSubtotal={
              computeExclusiveStaffEconomics(
                proposalState.nodeSets,
                proposalState.profitMargin,
                proposalState.globalCosts,
                {
                  isKitProposal: proposalState.isKitProposal,
                  kitPricing: proposalState.kitPricing,
                  activeSpecies: proposalState.activeSpecies,
                  discountAmount: getProposalDiscountAmount(proposalState.discount),
                  designEstimating: proposalState.designEstimating,
                  designEstimatingRates: rates,
                  proposalStatus: proposalData?.status,
                },
              ).defaultEconomics.billableTotal
            }
            onSave={(gc, nextEstimating) => {
              isLocalEditRef.current = true;
              setProposalState(prev => {
                if (!prev) return prev;
                const nodeSets = rates
                  ? syncNodeSetsForProjectOverhead(prev.nodeSets, gc, rates)
                  : prev.nodeSets;
                const stampedEstimating = isApproved
                  ? prev.designEstimating
                  : (rates
                    ? stampDesignEstimatingTotal(nextEstimating, rates, proposalData?.status)
                    : nextEstimating);
                const nextState = {
                  ...prev,
                  globalCosts: gc,
                  nodeSets,
                  designEstimating: stampedEstimating,
                };
                proposalStateRef.current = nextState;
                return nextState;
              });
              setHasChanges(true);
              setShowGlobalCosts(false);
            }}
            onClose={() => setShowGlobalCosts(false)}
          />
        )
      }
      {/* Promotional Discount Modal */}
      {
        showDiscountModal && proposalState && (
          <ProposalDiscountModal
            discount={proposalState.discount}
            billableTotal={
              computeExclusiveStaffEconomics(
                proposalState.nodeSets,
                proposalState.profitMargin,
                proposalState.globalCosts,
                {
                  isKitProposal: proposalState.isKitProposal,
                  kitPricing: proposalState.kitPricing,
                  activeSpecies: proposalState.activeSpecies,
                  designEstimating: proposalState.designEstimating,
                  designEstimatingRates: rates,
                  proposalStatus: proposalData?.status,
                },
              ).defaultEconomics.billableTotal
            }
            readOnly={isSent}
            onSave={(d) => {
              isLocalEditRef.current = true;
              const updatedState = { ...proposalState, discount: d };
              setProposalState(updatedState);
              setShowDiscountModal(false);
              if (!isSignedCommercial) {
                void saveProposalInternal(updatedState);
              }
            }}
            onClose={() => setShowDiscountModal(false)}
          />
        )
      }
      {showTimberSupplierSummary && proposalState && (
        <TimberSupplierSummaryModal
          nodeSets={proposalState.nodeSets}
          isKitProposal={proposalState.isKitProposal}
          activeSpecies={proposalState.activeSpecies}
          onClose={() => setShowTimberSupplierSummary(false)}
        />
      )}
      {isStaffBuilderUser && dealId && (
        <ProposalVersionHistoryModal
          open={showVersionHistory}
          onClose={() => setShowVersionHistory(false)}
          dealId={dealId}
          onSaveCurrentVersion={async (label: string) => {
            const saved = await saveProposalInternal();
            if (!saved) {
              alert('Could not save the proposal. Fix any issues and try again.');
              throw new Error('save failed');
            }
            await createProposalVersion(dealId, saved, {
              label: label || undefined,
              source: 'manual',
              createdBy: user?.email || user?.id || 'unknown',
            });
          }}
          onOpenSnapshot={v => {
            setSnapshotPreview(v);
            setShowVersionHistory(false);
          }}
        />
      )}
      {snapshotPreview?.snapshot && (
        <ProposalReviewModal
          open={!!snapshotPreview}
          onClose={() => setSnapshotPreview(null)}
          onSend={async () => {}}
          clientName={snapshotPreview.snapshot.clientName || ''}
          clientEmail={snapshotPreview.snapshot.clientEmail || ''}
          proposalSlug={snapshotPreview.snapshot.slug || ''}
          nodeSets={snapshotPreview.snapshot.nodeSets || []}
          profitMargin={snapshotPreview.snapshot.profitMargin ?? 40}
          sending={false}
          savedMessage=""
          onMessageChange={() => {}}
          savedRecipients={[]}
          onRecipientsChange={() => {}}
          globalCosts={snapshotPreview.snapshot.globalCosts}
          isKitProposal={snapshotPreview.snapshot.isKitProposal}
          kitPricing={snapshotPreview.snapshot.kitPricing}
          activeSpecies={snapshotPreview.snapshot.activeSpecies}
          snapshotProposal={snapshotPreview.snapshot}
          snapshotMode
          snapshotSubtitle={`${formatProposalVersionTimestamp(snapshotPreview)} · ${snapshotPreview.label || formatProposalVersionSource(snapshotPreview.source)}`}
        />
      )}

      {dealId && (
        <>
          <OpsReviewCommentsPanel
            open={showOpsCommentsPanel}
            onClose={() => setShowOpsCommentsPanel(false)}
            dealId={dealId}
            dealTitle={deal?.title}
            meta={opsReviewMeta}
            comments={opsReviewComments}
            canRequestReview={canRequestOpsReview}
            onPrepareApproval={async () => {
              const saved = await saveProposalInternal();
              if (!saved) {
                throw new Error('Could not save proposal before OPS approval.');
              }
              return createProposalVersion(dealId, saved, {
                label: 'OPS approved',
                source: 'ops_approved',
                createdBy: user?.email || user?.id || 'unknown',
              });
            }}
            onOpenVersion={async (versionId) => {
              const version = await getProposalVersion(dealId, versionId);
              if (!version?.snapshot) {
                alert('Could not load that proposal version.');
                return;
              }
              setSnapshotPreview(version);
              setShowOpsCommentsPanel(false);
            }}
            defaultDealId={dealId}
            defaultStructures={opsDefaultStructures}
            projectOptions={opsStructureMentionProjectOptions}
          />
        </>
      )}
    </div >
  );
};

export default ProposalBuilderInner;
